import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import API_URL from '../config/api';
import { MdDashboard, MdPeople, MdFitnessCenter, MdList, MdLogout, MdMenu, MdClose, MdArrowBack, MdAdd, MdSearch, MdClear, MdEmail, MdPhone, MdCake, MdPerson, MdCalendarToday, MdUpdate, MdEvent, MdAssignment, MdHistory, MdEdit, MdDelete, MdImage, MdPhotoCamera, MdWarning, MdVisibility, MdSmartToy } from 'react-icons/md';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ConversationalWorkoutGenerator from '../components/ConversationalWorkoutGenerator';
import WorkoutPlanViewer from '../components/WorkoutPlanViewer';
import ClientFilter from '../components/ClientFilter';
import ConfirmDialog from '../components/ConfirmDialog';
import { calculateAllBodyComposition } from '../utils/bodyComposition';
import EmailInputDialog from '../components/EmailInputDialog';
import './Dashboard.css';

function Dashboard() {
  const location = useLocation();
  const [activeSection, setActiveSection] = useState(() => location.state?.initialSection || 'dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedClient, setSelectedClient] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    const sectionFromState = location.state?.initialSection;
    if (sectionFromState && sectionFromState !== activeSection) {
      setActiveSection(sectionFromState);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, activeSection, navigate]);


  // Global state for clients
  const [clientsData, setClientsData] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsError, setClientsError] = useState('');

  // Global state for machines
  const [machinesData, setMachinesData] = useState([]);
  const [machinesLoading, setMachinesLoading] = useState(false);
  const [machinesError, setMachinesError] = useState('');

  // Global state for muscle groups (static data)
  const [muscleGroupsData, setMuscleGroupsData] = useState({});
  const [muscleGroupsLoading, setMuscleGroupsLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [muscleGroupsError, setMuscleGroupsError] = useState('');

  // Global state for exercises
  const [exercisesData, setExercisesData] = useState([]);
  const [exercisesLoading, setExercisesLoading] = useState(false);
  const [exercisesError, setExercisesError] = useState('');
  const [exerciseGroups, setExerciseGroups] = useState([]);

  // Gestione responsive
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load all necessary data once when Dashboard mounts
  useEffect(() => {
    const loadAllData = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Load Clients
      setClientsLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/client`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (response.ok) {
          setClientsData(data.clients || []);
        } else {
          setClientsError(data.error || 'Errore durante il caricamento dei clienti');
        }
      } catch (error) {
        setClientsError('Errore di connessione al server per i clienti');
      } finally {
        setClientsLoading(false);
      }

      // Load Machines
      setMachinesLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/client/machines`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (response.ok) {
          setMachinesData(data.machines || []);
        } else {
          setMachinesError(data.error || 'Errore durante il caricamento dei macchinari');
        }
      } catch (error) {
        setMachinesError('Errore di connessione al server per i macchinari');
      } finally {
        setMachinesLoading(false);
      }

      // Load Muscle Groups
      setMuscleGroupsLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/client/machines/groups`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (response.ok) {
          setMuscleGroupsData(data.muscleGroups || {});
        } else {
          setMuscleGroupsError(data.error || 'Errore durante il caricamento dei gruppi muscolari');
        }
      } catch (error) {
        setMuscleGroupsError('Errore di connessione al server per i gruppi muscolari');
      } finally {
        setMuscleGroupsLoading(false);
      }

      // Load Exercises
      setExercisesLoading(true);
      try {
        const [exRes, groupsRes] = await Promise.all([
          fetch(`${API_URL}/api/training/exercises`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${API_URL}/api/training/exercises/groups`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } })
        ]);
        const exData = await exRes.json();
        const groupsData = await groupsRes.json();
        if (exRes.ok) setExercisesData(exData.exercises || []);
        else setExercisesError(exData.error || 'Errore caricamento esercizi');
        if (groupsRes.ok) setExerciseGroups(groupsData.groups || []);
      } catch (error) {
        setExercisesError('Errore di connessione al server per gli esercizi');
      } finally {
        setExercisesLoading(false);
      }
    };

    loadAllData();
  }, [navigate]);

  const refreshClients = () => {
    setClientsLoading(true);
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/api/client`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` }
    })
    .then(res => res.json())
    .then(data => {
      if (data.clients) setClientsData(data.clients);
      else setClientsError(data.error || 'Errore durante il refresh dei clienti');
    })
    .catch(error => setClientsError('Errore di connessione al server per il refresh clienti'))
    .finally(() => setClientsLoading(false));
  };

  const refreshMachines = () => {
    setMachinesLoading(true);
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/api/client/machines`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` }
    })
    .then(res => res.json())
    .then(data => {
      if (data.machines) setMachinesData(data.machines);
      else setMachinesError(data.error || 'Errore durante il refresh dei macchinari');
    })
    .catch(error => setMachinesError('Errore di connessione al server per il refresh macchinari'))
    .finally(() => setMachinesLoading(false));
  };

  const refreshExercises = () => {
    setExercisesLoading(true);
    const token = localStorage.getItem('token');
    Promise.all([
      fetch(`${API_URL}/api/training/exercises`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } }),
      fetch(`${API_URL}/api/training/exercises/groups`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } })
    ])
    .then(([exRes, groupsRes]) => Promise.all([exRes.json(), groupsRes.json()]))
    .then(([exData, groupsData]) => {
      if (exData.exercises) setExercisesData(exData.exercises);
      else setExercisesError(exData.error || 'Errore refresh esercizi');
      if (groupsData.groups) setExerciseGroups(groupsData.groups);
    })
    .catch(() => setExercisesError('Errore di connessione al server per gli esercizi'))
    .finally(() => setExercisesLoading(false));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMenuClick = (section) => {
    setActiveSection(section);
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  };

  // Render del contenuto in base alla sezione attiva
  const renderContent = () => {
    switch (activeSection) {
      case 'clienti':
        return <ClientiSection 
          clients={clientsData} 
          loading={clientsLoading} 
          error={clientsError}
          onRefresh={refreshClients}
          onClientClick={(client) => {
            setSelectedClient(client);
            setActiveSection('dettagli-cliente');
          }} 
        />;
      case 'dettagli-cliente':
        return <DettagliClienteSection
          client={selectedClient}
          machinesData={machinesData}
          exercisesData={exercisesData}
          exerciseGroups={exerciseGroups}
          onBack={() => {
            setSelectedClient(null);
            setActiveSection('clienti');
          }}
          onRefreshClient={refreshClients}
        />;
      case 'macchinari':
        return <MacchinariSection 
          machines={machinesData} 
          loading={machinesLoading} 
          muscleGroups={muscleGroupsData}
          loadingMuscleGroups={muscleGroupsLoading}
          error={machinesError}
          onRefresh={refreshMachines}
        />;
      case 'esercizi':
        return <EserciziSection 
          exercises={exercisesData}
          loading={exercisesLoading}
          error={exercisesError}
          exerciseGroups={exerciseGroups}
          machines={machinesData}
          onRefresh={refreshExercises}
        />;
      case 'schede':
        return <SchedeSection clients={clientsData} machinesData={machinesData} exercisesData={exercisesData} exerciseGroups={exerciseGroups} />;
      default:
        return <DashboardSection 
          totalClients={clientsData.length}
          totalMachines={machinesData.length}
          clientsLoading={clientsLoading}
          machinesLoading={machinesLoading}
        />;
    }
  };

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: MdDashboard },
    { id: 'clienti', label: 'Clienti', icon: MdPeople },
    { id: 'macchinari', label: 'Macchinari', icon: MdFitnessCenter },
    { id: 'esercizi', label: 'Esercizi', icon: MdList },
    { id: 'schede', label: 'Schede', icon: MdAssignment }
  ];

  return (
    <div className="dashboard-container">
      {/* Mobile Menu Overlay */}
      {isMobile && isMobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={toggleMobileMenu}
        />
      )}

      {/* Hamburger Menu Button - Mobile Only */}
      {isMobile && (
        <button
          onClick={toggleMobileMenu}
          className="hamburger-button"
        >
          {isMobileMenuOpen ? <MdClose /> : <MdMenu />}
        </button>
      )}

      {/* Sidebar */}
      <div className={`sidebar ${isMobile ? (isMobileMenuOpen ? 'mobile-open' : 'mobile-closed') : 'desktop'}`}>
        <div className="logo-section">
          <img 
            src="/images/503547065_18084417088686417_5527300001175770798_n.jpg" 
            alt="Kinesis Fit Logo" 
            className="logo-image"
            onError={(e) => {
              console.error('Errore nel caricamento del logo:', e);
              e.target.style.display = 'none';
            }}
          />
        </div>
        
        <nav className="flex-1">
          {menuItems.map((item) => {
            const isActive = activeSection === item.id;
            const IconComponent = item.icon;
            return (
              <div
                key={item.id}
                className={`menu-item ${isActive ? 'active' : ''}`}
                onClick={() => handleMenuClick(item.id)}
              >
                <IconComponent className="menu-item-icon" />
                <span>{item.label}</span>
                {isActive && (
                  <div className="menu-item-indicator"></div>
                )}
              </div>
            );
          })}
        </nav>
        
        {/* Logout Button */}
        <div className="logout-section">
          <div
            className="logout-button"
            onClick={() => {
              handleLogout();
              if (isMobile) {
                setIsMobileMenuOpen(false);
              }
            }}
          >
            <MdLogout className="icon-large" />
            <span>Logout</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`content ${isMobile ? 'mobile' : 'desktop'}`}>
        {renderContent()}
      </div>
    </div>
  );
}

// Sezione Dashboard
function DashboardSection({ totalClients: propTotalClients, totalMachines: propTotalMachines, clientsLoading, machinesLoading }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [stats, setStats] = useState({
    totalClients: propTotalClients || 0,
    averageAge: null,
    totalMachines: propTotalMachines || 0,
    workoutPlansCreated: 0,
    monthlyClientsData: []
  });
  const [statsLoading, setStatsLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [statsError, setStatsError] = useState('');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Carica le statistiche dal backend
  useEffect(() => {
    const loadStats = async () => {
      setStatsLoading(true);
      setStatsError('');
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/client/dashboard/stats`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();

        if (response.ok) {
          setStats(data.statistics || {
            totalClients: 0,
            averageAge: null,
            totalMachines: 0,
            workoutPlansCreated: 0,
            monthlyClientsData: []
          });
        } else {
          setStatsError(data.error || 'Errore durante il caricamento delle statistiche');
        }
      } catch (error) {
        setStatsError('Errore di connessione al server');
      } finally {
        setStatsLoading(false);
      }
    };

    loadStats();
  }, []);

  // Usa i dati dalle props se disponibili (per compatibilità), altrimenti usa i dati caricati
  const totalClients = propTotalClients !== undefined ? propTotalClients : stats.totalClients;
  const totalMachines = propTotalMachines !== undefined ? propTotalMachines : stats.totalMachines;
  const averageAge = stats.averageAge;
  const workoutPlansCreated = stats.workoutPlansCreated;
  const monthlyClientsData = stats.monthlyClientsData.length > 0 
    ? stats.monthlyClientsData 
    : [
        { month: 'Gen', clients: 0 },
        { month: 'Feb', clients: 0 },
        { month: 'Mar', clients: 0 },
        { month: 'Apr', clients: 0 },
        { month: 'Mag', clients: 0 },
        { month: 'Giu', clients: 0 },
        { month: 'Lug', clients: 0 },
        { month: 'Ago', clients: 0 },
        { month: 'Set', clients: 0 },
        { month: 'Ott', clients: 0 },
        { month: 'Nov', clients: 0 },
        { month: 'Dic', clients: 0 },
      ];

  return (
    <>
      <h1 className={`section-title ${isMobile ? 'mobile' : ''} mb-30`}>
        Dashboard
      </h1>

      {/* Statistiche */}
      <div className={`card-grid ${isMobile ? 'mobile' : 'desktop'} mb-40`}>
        <div className="card stat-card">
          <div className="stat-card-content">
            <div className="stat-card-label">
              Clienti Totali
            </div>
            <div className="stat-card-value">
              {statsLoading ? '...' : totalClients}
            </div>
            <div className="stat-card-subtitle">
              Clienti attivi
            </div>
          </div>
        </div>

        <div className="card stat-card">
          <div className="stat-card-content">
            <div className="stat-card-label">
              Età Media dei clienti
            </div>
            <div className="stat-card-value">
              {statsLoading ? '...' : (averageAge !== null ? averageAge : 'N/A')}
            </div>
            <div className="stat-card-subtitle">
              Anni
            </div>
          </div>
        </div>

        <div className="card stat-card">
          <div className="stat-card-content">
            <div className="stat-card-label">
              Macchinari Totali
            </div>
            <div className="stat-card-value">
              {statsLoading ? '...' : totalMachines}
            </div>
            <div className="stat-card-subtitle">
              Aggiunti
            </div>
          </div>
        </div>

        <div className="card stat-card">
          <div className="stat-card-content">
            <div className="stat-card-label">
              Schede Create
            </div>
            <div className="stat-card-value">
              {statsLoading ? '...' : workoutPlansCreated}
            </div>
            <div className="stat-card-subtitle">
              Schede attive
            </div>
          </div>
        </div>
      </div>

      {/* Grafico Clienti Mensili */}
      <div className={`card chart-wrapper ${isMobile ? 'chart-wrapper-mobile' : ''}`}>
        <h3 className={`chart-title ${isMobile ? 'chart-title-mobile' : ''}`}>
          Andamento Clienti - Ultimi 12 Mesi
        </h3>
        <ResponsiveContainer width="100%" height={isMobile ? 280 : 350}>
          <LineChart 
            data={monthlyClientsData} 
            margin={isMobile ? { top: 5, right: 10, left: 0, bottom: 20 } : { top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(235, 205, 97, 0.1)" />
             <XAxis 
               dataKey="month" 
               stroke="rgba(255, 255, 255, 0.6)"
               className="chart-axis"
               tick={{ fontSize: isMobile ? 10 : 12 }}
               angle={isMobile ? -45 : 0}
               textAnchor={isMobile ? 'end' : 'middle'}
               height={isMobile ? 50 : 30}
             />
             <YAxis 
               stroke="rgba(255, 255, 255, 0.6)"
               className="chart-axis"
               tick={{ fontSize: isMobile ? 10 : 12 }}
               width={isMobile ? 35 : 50}
             />
            <Tooltip 
              contentStyle={{
                backgroundColor: '#1b1b1b',
                border: '1px solid rgba(235, 205, 97, 0.3)',
                borderRadius: '8px',
                color: '#ffffff',
                fontSize: isMobile ? '12px' : '14px',
                padding: isMobile ? '8px' : '12px'
              }}
              labelStyle={{ color: '#ebcd61', fontSize: isMobile ? '11px' : '13px' }}
            />
            <Legend 
              wrapperStyle={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: isMobile ? '11px' : '14px',
                paddingTop: isMobile ? '10px' : '0'
              }}
              iconSize={isMobile ? 12 : 14}
            />
            <Line 
              type="monotone" 
              dataKey="clients" 
              stroke="#ebcd61" 
              strokeWidth={isMobile ? 2 : 3}
              dot={{ fill: '#ebcd61', r: isMobile ? 3 : 5 }}
              activeDot={{ r: isMobile ? 5 : 7, fill: '#f5d876' }}
              name="Numero Clienti"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </>
  );
}

// Helper per formattare la data dell'ultima plicometria
const formatLastPlicometryDate = (latestMeasurement) => {
  if (!latestMeasurement || !latestMeasurement.measurement_date) {
    return { date: null, isOld: true }; // Nessuna plicometria registrata - mostrare in rosso
  }

  const measurementDate = new Date(latestMeasurement.measurement_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  measurementDate.setHours(0, 0, 0, 0);

  // Calcola i giorni passati dalla misurazione
  const daysSinceMeasurement = Math.floor((today - measurementDate) / (1000 * 60 * 60 * 24));
  
  // 3 mesi = circa 90 giorni
  const threeMonthsInDays = 90;
  const isOld = daysSinceMeasurement > threeMonthsInDays;

  // Formatta la data in italiano
  const formattedDate = measurementDate.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  return { date: formattedDate, isOld };
};


// Sezione Clienti
function ClientiSection({ clients = [], loading = false, error = '', onRefresh, onClientClick }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    email: '',
    telefono: '',
    data_nascita: '',
    sesso: 'M',
    peso: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Cliente creato con successo!');
        // Reset form
        setFormData({
          nome: '',
          cognome: '',
          email: '',
          telefono: '',
          data_nascita: '',
          sesso: 'M',
          peso: ''
        });
        // Ricarica la lista clienti tramite prop
        if (onRefresh) onRefresh();
        
        setTimeout(() => {
          setShowModal(false);
          setMessage('');
        }, 1500);
      } else {
        setMessage(data.error || 'Errore durante la creazione del cliente');
      }
    } catch (error) {
      setMessage('Errore di connessione al server');
    } finally {
      setFormLoading(false);
    }
  };


  // Filtra i clienti in base alla ricerca
  const filteredClients = clients.filter(client => {
    if (searchTerm === '') return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (client.nome && client.nome.toLowerCase().includes(searchLower)) ||
      (client.cognome && client.cognome.toLowerCase().includes(searchLower)) ||
      (client.email && client.email.toLowerCase().includes(searchLower)) ||
      (client.telefono && client.telefono.toLowerCase().includes(searchLower))
    );
  });

  return (
    <>
      <div className={`section-header ${isMobile ? 'mobile' : ''}`}>
        <h1 className={`section-title ${isMobile ? 'mobile' : ''}`}>
          Clienti
        </h1>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary"
        >
          + Aggiungi Cliente
        </button>
      </div>

      {/* Barra di ricerca */}
      <div className="search-container">
        <MdSearch className="search-icon" />
        <input
          type="text"
          placeholder="Cerca clienti"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="input-field input-search"
        />
      </div>

       {loading ? (
         <div className="loading-text">
           Caricamento clienti...
         </div>
       ) : filteredClients.length === 0 ? (
         <div className="empty-state">
           {searchTerm ? 'Nessun cliente trovato con i criteri di ricerca.' : 'Nessun cliente presente. Clicca su "Aggiungi Cliente" per iniziare.'}
         </div>
      ) : (
         <div className={`card-grid ${isMobile ? 'mobile' : 'desktop'}`}>
           {filteredClients.map((client) => (
            <div
              key={client.id}
               className="card"
              onClick={() => onClientClick(client)}
             >
               <h3 className="client-card-title">
                {client.nome} {client.cognome}
              </h3>
              <div style={{ flex: '1 1 auto', minHeight: '60px', minWidth: 0, overflow: 'hidden' }}>
                {client.email && (
                   <p className="client-info-row" style={{ width: '100%' }}>
                     <MdEmail className="icon-small" style={{ flexShrink: 0 }} />
                     <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{client.email}</span>
                  </p>
                )}
                {client.telefono && (
                   <p className="client-info-row mb-12" style={{ width: '100%' }}>
                     <MdPhone className="icon-small" style={{ flexShrink: 0 }} />
                     <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{client.telefono}</span>
                   </p>
                 )}
              </div>
               
              {/* Informazioni plicometria e scheda */}
              <div className="client-info-divider">
                {/* Informazioni ultima plicometria */}
                {(() => {
                  const lastPlicometry = formatLastPlicometryDate(client.latestMeasurement);
                  const shouldShowRed = !lastPlicometry.date || lastPlicometry.isOld;
                  const redColor = '#ff6b6b';
                  
                  if (lastPlicometry.date) {
                    return (
                      <div className="client-checkup-info">
                        <MdEvent className="icon-small" style={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                        <span className="client-checkup-text" style={{ color: shouldShowRed ? redColor : 'rgba(255, 255, 255, 0.7)' }}>
                          Ultima plicometria: <span className="client-checkup-value" style={{ color: shouldShowRed ? redColor : '#ebcd61', fontWeight: 500 }}>{lastPlicometry.date}</span>
                        </span>
                      </div>
                    );
                  } else {
                    return (
                      <div className="client-checkup-info">
                        <MdEvent className="icon-small" style={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                        <span className="client-checkup-text" style={{ color: redColor }}>
                          Nessuna plicometria registrata
                        </span>
                      </div>
                    );
                  }
                })()}
                
                {/* Informazioni scheda */}
                {(() => {
                  if (client.activePlan && client.activePlan.count > 0) {
                    const planCount = client.activePlan.count;
                    return (
                      <div className="client-checkup-info">
                        <MdAssignment className="icon-small" style={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                        <span className="client-checkup-text">
                          {planCount === 1 ? '1 scheda attiva' : `${planCount} schede attive`}
                        </span>
                      </div>
                    );
                  } else {
                    return (
                      <div className="client-checkup-info">
                        <MdAssignment className="icon-small" style={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                        <span className="client-checkup-text">
                          Nessuna scheda attiva
                        </span>
                      </div>
                    );
                  }
                })()}
              </div>

               <div className="client-info-footer">
                 <p className="client-info-footer-text">
                  Creato il: {new Date(client.created_at).toLocaleDateString('it-IT')}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal per aggiungere cliente */}
      {showModal && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowModal(false);
            }
          }}
        >
          <div className="modal-content" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2 className="modal-title">
                Aggiungi Nuovo Cliente
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="modal-close"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
           <div className={`grid-2 ${isMobile ? 'mobile' : ''} mb-20`}>
             <div>
               <label className="label">Nome *</label>
               <input
                 type="text"
                 name="nome"
                 value={formData.nome}
                 onChange={handleChange}
                 required
                 className="input-field"
                 placeholder="Nome"
               />
             </div>
             <div>
               <label className="label">Cognome *</label>
               <input
                 type="text"
                 name="cognome"
                 value={formData.cognome}
                 onChange={handleChange}
                 required
                 className="input-field"
                 placeholder="Cognome"
               />
             </div>
           </div>

           <div className={`grid-2 ${isMobile ? 'mobile' : ''} mb-20`}>
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="input-field"
                placeholder="email@example.com"
              />
            </div>
            <div>
              <label className="label">Telefono</label>
              <input
                type="tel"
                name="telefono"
                value={formData.telefono}
                onChange={handleChange}
                className="input-field"
                placeholder="+39 123 456 7890"
              />
            </div>
          </div>

          <div className={`grid-2 ${isMobile ? 'mobile' : ''} mb-20`}>
            <div>
              <label className="label">Data di Nascita</label>
              <input
                type="date"
                name="data_nascita"
                value={formData.data_nascita}
                onChange={handleChange}
                className="input-field"
              />
            </div>
            <div>
              <label className="label">Sesso</label>
              <select
                name="sesso"
                value={formData.sesso}
                onChange={handleChange}
                className="input-field"
              >
                <option value="M">Maschio</option>
                <option value="F">Femmina</option>
              </select>
            </div>
          </div>
          
          <div className="mb-20">
             <label className="label">Peso (kg)</label>
             <input
               type="number"
               name="peso"
               value={formData.peso}
               onChange={handleChange}
               step="0.1"
               className="input-field"
               placeholder="Es. 75.5"
             />
          </div>


              <button type="submit" disabled={formLoading} className="btn-primary mt-20" style={{ opacity: formLoading ? 0.6 : 1, cursor: formLoading ? 'not-allowed' : 'pointer' }}>
                 {formLoading ? 'Creazione...' : 'Crea Cliente'}
              </button>

              {message && (
                <div className={`message ${message.includes('successo') ? 'success' : 'error'}`}>
                  {message}
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </>
  );
}

// Sezione Dettagli Cliente
function DettagliClienteSection({ client, onBack, onRefreshClient, machinesData = [], exercisesData = [], exerciseGroups = [] }) {
  const measurementFields = [
    { key: 'pettorale', label: 'Pettorale (mm)' },
    { key: 'tricipite', label: 'Tricipite (mm)' },
    { key: 'ascellare', label: 'Ascellare (mm)' },
    { key: 'scapolare', label: 'Scapolare (mm)' },
    { key: 'soprailiaca', label: 'Soprailiaca (mm)' },
    { key: 'addominale', label: 'Addominale (mm)' },
    { key: 'coscia', label: 'Coscia (mm)' },
    { key: 'bicipite', label: 'Bicipite (mm)' },
  ];

  const circumferenceFields = [
    { key: 'circ_spalle', label: 'Spalle (cm)' },
    { key: 'circ_petto', label: 'Petto (cm)' },
    { key: 'circ_braccio', label: 'Braccio (cm)' },
    { key: 'circ_vita', label: 'Vita (cm)' },
    { key: 'circ_fianchi', label: 'Fianchi (cm)' },
    { key: 'circ_coscia', label: 'Coscia (cm)' },
    { key: 'circ_polpaccio', label: 'Polpaccio (cm)' },
  ];

  const getInitialMeasurementForm = () => ({
    measurement_date: new Date().toISOString().slice(0, 16),
    note: '',
    peso: selectedClient?.peso || '',
    pettorale: '',
    tricipite: '',
    ascellare: '',
    scapolare: '',
    soprailiaca: '',
    addominale: '',
    coscia: '',
    bicipite: '',
    circ_spalle: '',
    circ_petto: '',
    circ_braccio: '',
    circ_vita: '',
    circ_fianchi: '',
    circ_coscia: '',
    circ_polpaccio: '',
  });

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedClient, setSelectedClient] = useState(client);
  const [chartType, setChartType] = useState('massaGrassa'); // 'massaGrassa' o 'massaMagra'
  const [activeSection, setActiveSection] = useState('schede'); // 'schede' o 'plicometrie'
  const [measurements, setMeasurements] = useState([]);
  const [measurementsLoading, setMeasurementsLoading] = useState(false);
  const [measurementsError, setMeasurementsError] = useState('');
  const [measurementMessage, setMeasurementMessage] = useState('');
  const [measurementForm, setMeasurementForm] = useState(getInitialMeasurementForm());
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [deleteMeasurementConfirm, setDeleteMeasurementConfirm] = useState({ isOpen: false, measurementId: null });
  const [clientPlans, setClientPlans] = useState([]);
  const [clientPlansLoading, setClientPlansLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [deleteClientConfirm, setDeleteClientConfirm] = useState({ isOpen: false });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setSelectedClient(client);
  if (client && client.id) {
    fetchMeasurements(client.id);
    fetchClientPlans(client.id);
  } else {
    setMeasurements([]);
    setClientPlans([]);
  }
  }, [client]);

  // Carica le schede del cliente
  const fetchClientPlans = async (clientId) => {
    setClientPlansLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/${clientId}/plans`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClientPlans(data.plans || []);
      }
    } catch (error) {
      console.error('Errore caricamento schede cliente:', error);
    } finally {
      setClientPlansLoading(false);
    }
  };

  // Genera i dati del grafico basati sullo storico plicometrie
  const generateChartData = () => {
    if (!measurements || measurements.length === 0 || !selectedClient?.eta || !selectedClient?.sesso) {
      return [];
    }

    // Copia e ordina per data crescente
    const sorted = [...measurements].sort((a, b) => new Date(a.measurement_date) - new Date(b.measurement_date));

    // Filtra e mappa i dati
    const data = sorted.map(m => {
      // Calcola composizione per questo punto dati
      // Usiamo il peso attuale del cliente (approssimazione se non storicizzato)
      const result = calculateAllBodyComposition(m, selectedClient.eta, selectedClient.sesso, selectedClient.peso);
      
      console.log('Chart Point Debug:', {
        date: m.measurement_date,
        pliche: { p: m.pettorale, a: m.addominale, c: m.coscia, t: m.tricipite, s: m.soprailiaca },
        result
      });

      // Seleziona l'algoritmo migliore disponibile: JP7 > JP3 > DW
      let best = null;
      if (result.jacksonPollock7) best = result.jacksonPollock7;
      else if (result.jacksonPollock3) best = result.jacksonPollock3;
      else if (result.durninWomersley) best = result.durninWomersley;

      if (!best) return null;

      const date = new Date(m.measurement_date);
      const label = date.toLocaleDateString('it-IT', { 
        day: '2-digit', 
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });

      return {
        mese: label,
        metodo: result.jacksonPollock7 ? 'Jackson-Pollock 7' : (result.jacksonPollock3 ? 'Jackson-Pollock 3' : 'Durnin-Womersley'),
        massaGrassa: parseFloat(best.bodyFatPercentage.toFixed(1)), // Usa la % per coerenza con l'asse Y del grafico
        massaMagra: best.leanMass ? parseFloat(best.leanMass.toFixed(1)) : null,
        fatMassKg: best.fatMass ? parseFloat(best.fatMass.toFixed(1)) : null
      };
    }).filter(item => item !== null);

    return data;
  };

  const chartData = generateChartData();

  const fetchMeasurements = async (clientId) => {
    setMeasurementsLoading(true);
    setMeasurementsError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/${clientId}/plicometria`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (response.ok) {
        setMeasurements(data.measurements || []);
      } else {
        setMeasurementsError(data.error || 'Errore durante il recupero delle plicometrie');
      }
    } catch (error) {
      setMeasurementsError('Errore di connessione al server');
    } finally {
      setMeasurementsLoading(false);
    }
  };

  // Stato per prevenire doppi invii
  const [isSubmittingMeasurement, setIsSubmittingMeasurement] = useState(false);

  const handleMeasurementChange = (e) => {
    const { name, value } = e.target;
    setMeasurementForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddMeasurement = async (e) => {
    e.preventDefault();
    if (isSubmittingMeasurement) return; // Previeni doppi click

    setIsSubmittingMeasurement(true);
    setMeasurementMessage('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/${selectedClient.id}/plicometria`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(measurementForm),
      });

      const data = await response.json();
      if (response.ok) {
        setMeasurementMessage('Plicometria registrata con successo!');
        setMeasurementForm(getInitialMeasurementForm());
        await fetchMeasurements(selectedClient.id);
        if (data.client) {
          setSelectedClient(data.client);
        }
        setTimeout(() => {
          setMeasurementMessage('');
          setShowAddDialog(false);
          setIsSubmittingMeasurement(false); // Riabilita dopo timeout
        }, 1500); // Mantiene disabilitato per 1.5s dopo successo per evitare click nervosi
      } else {
        setMeasurementMessage(data.error || 'Errore durante la registrazione della plicometria');
        // Riabilita subito in caso di errore logico ma aspetta 3s per "punizione" anti-spam? 
        // No, meglio riabilitare dopo un breve delay per evitare spam di errori
        setTimeout(() => setIsSubmittingMeasurement(false), 3000);
      }
    } catch (error) {
      setMeasurementMessage('Errore di connessione al server');
      setTimeout(() => setIsSubmittingMeasurement(false), 3000);
    }
  };

  const latestMeasurement =
    selectedClient?.latestMeasurement || (measurements.length > 0 ? measurements[0] : null);

  const handleDeleteMeasurement = async (measurementId) => {
    setDeleteMeasurementConfirm({ isOpen: true, measurementId });
  };

  const confirmDeleteMeasurement = async () => {
    const { measurementId } = deleteMeasurementConfirm;
    if (!measurementId) return;

    setDeleteMeasurementConfirm({ isOpen: false, measurementId: null });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/plicometria/${measurementId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setMeasurementMessage('Misurazione eliminata con successo!');
        await fetchMeasurements(selectedClient.id);
        setTimeout(() => setMeasurementMessage(''), 3000);
      } else {
        const data = await response.json();
        setMeasurementMessage(data.error || 'Errore durante l\'eliminazione della misurazione');
      }
    } catch (error) {
      setMeasurementMessage('Errore di connessione al server');
    }
  };

  // DEBUG LOG
  if (selectedClient) {
    console.log('DettagliCliente - Dati per calcolo:', {
      eta: selectedClient.eta,
      sesso: selectedClient.sesso,
      peso: selectedClient.peso,
      hasMeasurement: !!latestMeasurement,
      measurement: latestMeasurement
    });
  }

  const bodyComposition = latestMeasurement && selectedClient?.eta && selectedClient?.sesso
    ? calculateAllBodyComposition(latestMeasurement, selectedClient.eta, selectedClient.sesso, selectedClient.peso)
    : null;
    
  console.log('DettagliCliente - Risultato calcolo:', bodyComposition);

  const clientDataForViewer = (() => {
    if (!selectedClient) return null;
    const bc = bodyComposition;
    const bestMethod = bc?.jacksonPollock7 || bc?.jacksonPollock3 || bc?.durninWomersley;

    const data = {
      nome: selectedClient.nome,
      cognome: selectedClient.cognome,
      peso: selectedClient.peso ? parseFloat(selectedClient.peso) : null,
      eta: selectedClient.eta,
      sesso: selectedClient.sesso,
    };

    if (bestMethod) {
      data.bodyComposition = {
        percentuale_grasso: Math.round(bestMethod.bodyFatPercentage * 10) / 10,
        massa_grassa_kg: bestMethod.fatMass !== null ? Math.round(bestMethod.fatMass * 10) / 10 : null,
        massa_magra_kg: bestMethod.leanMass !== null ? Math.round(bestMethod.leanMass * 10) / 10 : null,
      };
    }

    if (latestMeasurement) {
      data.pliche = {
        pettorale: parseFloat(latestMeasurement.pettorale) || null,
        tricipite: parseFloat(latestMeasurement.tricipite) || null,
        ascellare: parseFloat(latestMeasurement.ascellare) || null,
        scapolare: parseFloat(latestMeasurement.scapolare) || null,
        soprailiaca: parseFloat(latestMeasurement.soprailiaca) || null,
        addominale: parseFloat(latestMeasurement.addominale) || null,
        coscia: parseFloat(latestMeasurement.coscia) || null,
        bicipite: parseFloat(latestMeasurement.bicipite) || null,
      };
      data.circonferenze = {
        spalle: parseFloat(latestMeasurement.circ_spalle) || null,
        petto: parseFloat(latestMeasurement.circ_petto) || null,
        braccio: parseFloat(latestMeasurement.circ_braccio) || null,
        vita: parseFloat(latestMeasurement.circ_vita) || null,
        fianchi: parseFloat(latestMeasurement.circ_fianchi) || null,
        coscia: parseFloat(latestMeasurement.circ_coscia) || null,
        polpaccio: parseFloat(latestMeasurement.circ_polpaccio) || null,
      };
      data.measurementDate = latestMeasurement.measurement_date;
    }

    if (!data.bodyComposition && !data.pliche && !data.circonferenze) return null;
    return data;
  })();

  const handleEditChange = (e) => {
    setEditData({
      ...editData,
      [e.target.name]: e.target.value
    });
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditData({
      nome: selectedClient.nome || '',
      cognome: selectedClient.cognome || '',
      email: selectedClient.email || '',
      telefono: selectedClient.telefono || '',
      eta: selectedClient.eta || '',
      data_nascita: selectedClient.data_nascita || '',
      sesso: selectedClient.sesso || '',
      peso: selectedClient.peso || '',
    });
    setMessage('');
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditData({});
    setMessage('');
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/${selectedClient.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editData)
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Cliente aggiornato con successo!');
        if (onRefreshClient) onRefreshClient();

        setTimeout(() => {
          setSelectedClient(data.client);
          setIsEditing(false);
          setEditData({});
          setMessage('');
        }, 1500);
      } else {
        setMessage(data.error || 'Errore durante l\'aggiornamento del cliente');
      }
    } catch (error) {
      setMessage('Errore di connessione al server');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteClient = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/${selectedClient.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Cliente eliminato con successo!');
        // Torna alla lista clienti dopo un breve delay
        setTimeout(() => {
          onRefreshClient();
          onBack();
        }, 1000);
      } else {
        setMessage(data.error || 'Errore durante l\'eliminazione del cliente');
      }
    } catch (error) {
      setMessage('Errore di connessione al server');
    } finally {
      setDeleteClientConfirm({ isOpen: false });
    }
  };

  if (!selectedClient) {
    return null;
  }

  return (
    <>
      {/* Header con bottone indietro */}
       <div className="detail-header">
        <button
          onClick={onBack}
           className="back-button"
        >
          <MdArrowBack />
        </button>
         <h1 className={`detail-title ${isMobile ? 'mobile' : ''}`}>
          {isEditing ? 'Modifica Cliente' : `${selectedClient.nome} ${selectedClient.cognome}`}
        </h1>
        {!isEditing && (
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              onClick={handleEdit}
              className="edit-button"
            >
              Modifica
            </button>
            <button
              onClick={() => setDeleteClientConfirm({ isOpen: true })}
              style={{
                background: 'rgba(244, 67, 54, 0.1)',
                border: '1px solid rgba(244, 67, 54, 0.3)',
                color: '#f44336',
                padding: '10px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(244, 67, 54, 0.2)';
                e.target.style.borderColor = 'rgba(244, 67, 54, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(244, 67, 54, 0.1)';
                e.target.style.borderColor = 'rgba(244, 67, 54, 0.3)';
              }}
            >
              <MdDelete size={18} />
              Elimina
            </button>
          </div>
        )}
      </div>

      {isEditing ? (
        <form onSubmit={handleUpdateClient}>
          {/* Informazioni di contatto editabili */}
          <div className="form-section">
            <h3 className="form-section-title">
              Informazioni anagrafiche
            </h3>
            <div className={`grid-2 ${isMobile ? 'mobile' : ''}`} style={{ gap: '16px' }}>
              <div>
                <label className="label">
                  Nome *
                </label>
                <input
                  type="text"
                  name="nome"
                  value={editData.nome}
                  onChange={handleEditChange}
                  required
                  className="input-field"
                />
              </div>
              <div>
                <label className="label">
                  Cognome *
                </label>
                <input
                  type="text"
                  name="cognome"
                  value={editData.cognome}
                  onChange={handleEditChange}
                  required
                  className="input-field"
                />
              </div>
              <div>
                <label className="label">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={editData.email}
                  onChange={handleEditChange}
                  className="input-field"
                />
              </div>
              <div>
                <label className="label">
                  Telefono
                </label>
                <input
                  type="tel"
                  name="telefono"
                  value={editData.telefono}
                  onChange={handleEditChange}
                  className="input-field"
                />
              </div>
              <div>
                <label className="label">
                  Data di Nascita
                </label>
                <input
                  type="date"
                  name="data_nascita"
                  value={editData.data_nascita || ''}
                  onChange={handleEditChange}
                  className="input-field"
                />
              </div>
              <div>
                <label className="label">
                  Sesso
                </label>
                <select
                  name="sesso"
                  value={editData.sesso || ''}
                  onChange={handleEditChange}
                  className="input-field"
                >
                  <option value="">Seleziona</option>
                  <option value="M">Maschio</option>
                  <option value="F">Femmina</option>
                </select>
              </div>
              <div>
                <label className="label">
                  Peso (kg)
                </label>
                <input
                  type="number"
                  name="peso"
                  value={editData.peso || ''}
                  onChange={handleEditChange}
                  step="0.1"
                  className="input-field"
                />
              </div>
             </div>
           </div>

           {/* Pulsanti di azione */}
           <div className="form-actions">
             <button
               type="button"
               onClick={handleCancelEdit}
               disabled={editLoading}
               className="btn-cancel"
               style={{ cursor: editLoading ? 'not-allowed' : 'pointer' }}
             >
               Annulla
             </button>
             <button
               type="submit"
               disabled={editLoading}
               className="btn-primary"
               style={{ opacity: editLoading ? 0.6 : 1, cursor: editLoading ? 'not-allowed' : 'pointer' }}
             >
               {editLoading ? 'Salvataggio...' : 'Salva Modifiche'}
             </button>
           </div>

           {message && (
             <div className={`message ${message.includes('successo') ? 'success' : 'error'}`}>
               {message}
             </div>
           )}
         </form>
      ) : (
        <>
           {/* Informazioni anagrafiche e Grafico - Layout affiancato */}
           <div className={`two-column-layout ${isMobile ? 'mobile' : ''}`}>
             {/* Informazioni anagrafiche - Sempre visibili */}
             <div className="info-box">
             <h3 className="info-box-title">
              Informazioni anagrafiche
            </h3>
             <div className="info-list">
              {selectedClient.email && (
                 <div className="info-item">
                     <MdEmail className="info-icon" />
                   <span className="info-text">{selectedClient.email}</span>
                </div>
              )}
              {selectedClient.telefono && (
                 <div className="info-item">
                     <MdPhone className="info-icon" />
                   <span className="info-text">{selectedClient.telefono}</span>
                </div>
              )}
               <div className="info-item">
                   <MdCake className="info-icon" />
                   <span className="info-text">
                     Età: <span className={selectedClient.eta ? '' : 'info-text-placeholder'}>
                       {selectedClient.eta || 'Dato da aggiungere'}
                     </span>
                   </span>
                 </div>
                 <div className="info-item">
                   <MdPerson className="info-icon" />
                   <span className="info-text">
                     Sesso: <span className={selectedClient.sesso ? '' : 'info-text-placeholder'}>
                       {selectedClient.sesso || 'Dato da aggiungere'}
                     </span>
                   </span>
                 </div>
                 {selectedClient.peso && (
                   <div className="info-item">
                     <MdFitnessCenter className="info-icon" />
                     <span className="info-text">
                       Peso: <span>
                         {parseFloat(selectedClient.peso).toFixed(2)} kg
                       </span>
                     </span>
                   </div>
                 )}
                 <div className="info-item">
                   <MdCalendarToday className="info-icon" />
                 <span className="info-text">
                  Creato il: {new Date(selectedClient.created_at).toLocaleDateString('it-IT', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              {selectedClient.updated_at && selectedClient.updated_at !== selectedClient.created_at && (
                 <div className="info-item">
                     <MdUpdate className="info-icon" />
                   <span className="info-text">
                    Aggiornato il: {new Date(selectedClient.updated_at).toLocaleDateString('it-IT', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>

             {/* Grafico storico indici corporei */}
             <div className="info-box">
               <div className="chart-controls">
             <h3 className="chart-title-small">
                   Storico Indici Corporei
            </h3>
                 <div className="chart-toggle-group">
                   <button
                     onClick={() => setChartType('massaGrassa')}
                     className={`chart-toggle-button ${chartType === 'massaGrassa' ? 'active' : ''}`}
                   >
                     Massa Grassa
                   </button>
                   <button
                     onClick={() => setChartType('massaMagra')}
                     className={`chart-toggle-button ${chartType === 'massaMagra' ? 'active' : ''}`}
                   >
                     Massa Magra
                   </button>
                 </div>
               </div>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                    <XAxis 
                      dataKey="mese" 
                      stroke="rgba(255, 255, 255, 0.5)"
                      style={{ fontSize: '12px' }}
                    />
                    <YAxis 
                      stroke="rgba(255, 255, 255, 0.5)"
                      style={{ fontSize: '12px' }}
                      label={{ 
                        value: chartType === 'massaGrassa' ? 'Massa Grassa (%)' : 'Massa Magra (kg)', 
                        angle: -90, 
                        position: 'insideLeft',
                        style: { textAnchor: 'middle', fill: 'rgba(255, 255, 255, 0.7)', fontSize: '12px' }
                      }}
                    />
                    <Tooltip 
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          const isFat = chartType === 'massaGrassa';
                          return (
                            <div style={{ backgroundColor: '#1b1b1b', border: '1px solid rgba(235, 205, 97, 0.3)', padding: '10px', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
                              <p style={{ color: '#ebcd61', fontWeight: 'bold', marginBottom: '4px', fontSize: '14px' }}>{label}</p>
                              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px', marginBottom: '8px' }}>
                                Metodo: <span style={{ color: '#fff' }}>{data.metodo}</span>
                              </p>
                              <p style={{ color: '#fff', fontSize: '13px', margin: 0 }}>
                                {isFat ? 'Massa Grassa: ' : 'Massa Magra: '}
                                <span style={{ fontWeight: 'bold', color: '#4ade80' }}>
                                  {payload[0].value}
                                  {isFat ? '%' : ' kg'}
                                </span>
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Legend 
                      wrapperStyle={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey={chartType} 
                      stroke="#ebcd61" 
                      strokeWidth={2}
                      dot={{ fill: '#ebcd61', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic', flexDirection: 'column', gap: '10px' }}>
                  <MdWarning style={{ fontSize: '24px', opacity: 0.7 }} />
                  <span>Dati insufficienti per generare il grafico storico.</span>
                  <span style={{ fontSize: '12px', opacity: 0.7 }}>Assicurati che il cliente abbia età, sesso e plicometrie valide.</span>
                </div>
              )}
            </div>
          </div>

           {/* Sezioni Schede e Plicometrie */}
           <div className="tabs-container">
             {/* Selettori Tab */}
             <div className="tabs-header">
               <button
                 onClick={() => setActiveSection('schede')}
                 className={`tab-button ${activeSection === 'schede' ? 'active' : ''}`}
               >
                 Schede
               </button>
               <button
                 onClick={() => setActiveSection('plicometrie')}
                 className={`tab-button ${activeSection === 'plicometrie' ? 'active' : ''}`}
               >
                 Plicometrie
               </button>
             </div>

             {/* Contenuto della sezione attiva */}
             <div className="tabs-content">
               {activeSection === 'schede' ? (
                 <div style={{ padding: '20px 0' }}>
                   {clientPlansLoading ? (
                     <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.6)' }}>
                       Caricamento schede...
                     </div>
                   ) : clientPlans.length === 0 ? (
                     <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.6)' }}>
                       <p>Nessuna scheda di allenamento per questo cliente.</p>
                       <p style={{ fontSize: '14px', marginTop: '10px', opacity: 0.7 }}>
                         Crea una nuova scheda dalla sezione "Schede" principale.
                       </p>
                     </div>
                   ) : selectedPlan ? (
                     <div>
                       <div style={{ 
                         display: 'flex', 
                         justifyContent: 'space-between', 
                         alignItems: 'center', 
                         marginBottom: '24px',
                         paddingBottom: '20px',
                         borderBottom: '1px solid rgba(255,255,255,0.1)'
                       }}>
                         <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                           <button
                             onClick={() => setSelectedPlan(null)}
                             style={{
                               background: 'rgba(255,255,255,0.1)',
                               border: '1px solid rgba(255,255,255,0.2)',
                               color: 'white',
                               fontSize: '20px',
                               cursor: 'pointer',
                               width: '40px',
                               height: '40px',
                               borderRadius: '8px',
                               display: 'flex',
                               alignItems: 'center',
                               justifyContent: 'center',
                               transition: 'all 0.3s ease'
                             }}
                             onMouseEnter={(e) => {
                               e.target.style.background = 'rgba(255,255,255,0.2)';
                               e.target.style.transform = 'translateX(-4px)';
                             }}
                             onMouseLeave={(e) => {
                               e.target.style.background = 'rgba(255,255,255,0.1)';
                               e.target.style.transform = 'translateX(0)';
                             }}
                           >
                             <MdArrowBack />
                           </button>
                           <h3 style={{ margin: 0, color: '#fff', fontSize: '20px', fontWeight: '600' }}>
                             {selectedPlan.nome}
                           </h3>
                         </div>
                       </div>
                       <WorkoutPlanViewer
                         plan={selectedPlan.scheda_json}
                         machines={machinesData}
                         exercises={exercisesData}
                         exerciseGroups={exerciseGroups}
                         readOnly={true}
                         lastEmailSent={selectedPlan.last_email_sent_at}
                         lastEmailStatus={selectedPlan.last_email_status}
                         clientData={clientDataForViewer}
                       />
                     </div>
                   ) : (
                     <div>
                       <h4 style={{ marginBottom: '20px', color: '#fff', fontSize: '18px', fontWeight: '600' }}>
                         Schede di Allenamento ({clientPlans.length})
                       </h4>
                       <div style={{ 
                         display: 'grid', 
                         gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', 
                         gap: '20px' 
                       }}>
                         {clientPlans.map((plan) => (
                           <div
                             key={plan.id}
                             onClick={() => setSelectedPlan(plan)}
                             style={{
                               background: 'rgba(255,255,255,0.05)',
                               border: '1px solid rgba(255,255,255,0.1)',
                               borderRadius: '12px',
                               padding: '20px',
                               cursor: 'pointer',
                               transition: 'all 0.3s ease',
                               display: 'flex',
                               flexDirection: 'column',
                               minHeight: '180px'
                             }}
                             onMouseEnter={(e) => {
                               e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                               e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                               e.currentTarget.style.transform = 'translateY(-4px)';
                             }}
                             onMouseLeave={(e) => {
                               e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                               e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                               e.currentTarget.style.transform = 'translateY(0)';
                             }}
                           >
                             <h5 style={{ 
                               margin: '0 0 12px 0', 
                               color: '#fff', 
                               fontSize: '16px', 
                               fontWeight: '600' 
                             }}>
                               {plan.nome}
                             </h5>
                             {plan.obiettivi && (
                               <p style={{ 
                                 margin: '0 0 12px 0', 
                                 color: 'rgba(255,255,255,0.7)', 
                                 fontSize: '14px',
                                 lineHeight: '1.5'
                               }}>
                                 {plan.obiettivi}
                               </p>
                             )}
                             <div style={{ 
                               display: 'flex', 
                               gap: '12px', 
                               marginTop: 'auto',
                               fontSize: '13px',
                               color: 'rgba(255,255,255,0.6)'
                             }}>
                               {plan.durata_settimane && (
                                 <span>
                                   <MdCalendarToday style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                                   {plan.durata_settimane} sett.
                                 </span>
                               )}
                               {plan.frequenza_settimanale && (
                                 <span>
                                   <MdEvent style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                                   {plan.frequenza_settimanale}x/sett
                                 </span>
                               )}
                             </div>
                             {plan.data_scadenza && (
                               <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                 <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>
                                   <strong style={{ color: 'rgba(255,255,255,0.5)' }}>Scadenza:</strong>{' '}
                                   {new Date(plan.data_scadenza).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                                 </p>
                                 {plan.status && (
                                   <span style={{
                                     background: plan.status === 'Scaduta' 
                                       ? 'rgba(244, 67, 54, 0.2)' 
                                       : plan.status === 'In scadenza'
                                       ? 'rgba(255, 152, 0, 0.2)'
                                       : 'rgba(76, 175, 80, 0.2)',
                                     border: `1px solid ${plan.status === 'Scaduta' 
                                       ? 'rgba(244, 67, 54, 0.4)' 
                                       : plan.status === 'In scadenza'
                                       ? 'rgba(255, 152, 0, 0.4)'
                                       : 'rgba(76, 175, 80, 0.4)'}`,
                                     borderRadius: '16px',
                                     padding: '4px 10px',
                                     fontSize: '11px',
                                     fontWeight: '600',
                                     color: plan.status === 'Scaduta' 
                                       ? '#f44336' 
                                       : plan.status === 'In scadenza'
                                       ? '#ff9800'
                                       : '#4caf50',
                                     alignSelf: 'flex-start',
                                     display: 'inline-block'
                                   }}>
                                     {plan.status}
                                     {plan.giorni_rimanenti !== undefined && plan.giorni_rimanenti >= 0 && (
                                       <span style={{ marginLeft: '6px', opacity: 0.8 }}>
                                         ({plan.giorni_rimanenti} {plan.giorni_rimanenti === 1 ? 'giorno' : 'giorni'})
                                       </span>
                                     )}
                                   </span>
                                 )}
                               </div>
                             )}
                           </div>
                         ))}
                       </div>
                     </div>
                   )}
                 </div>
               ) : (
                 <div className="plicometry-section">
                   {/* Header con pulsanti */}
               <div className="plicometry-header">
                     <h4 className="plicometry-title">
                       Ultima Plicometria
                     </h4>
                   <div className="plicometry-actions">
                       <button
                         onClick={() => setShowHistoryDialog(true)}
                         className="plicometry-button"
                       >
                         <MdHistory className="icon-medium" />
                         <span>Visualizza Registro</span>
                       </button>
                       <button
                         onClick={() => setShowAddDialog(true)}
                         className="plicometry-button-primary"
                       >
                         <MdAdd className="icon-medium" />
                         <span>Aggiungi Nuova</span>
                       </button>
                    </div>
                    </div>

                   {/* Analisi Composizione Corporea */}
                   {latestMeasurement && (
                     <div className="plicometry-analysis-section" style={{ marginBottom: '30px' }}>
                       {!selectedClient.eta || !selectedClient.sesso ? (
                         <div className="message warning mb-20" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                           <MdWarning className="icon-medium" />
                           <span>Età e Sesso necessari per il calcolo. Modifica il cliente.</span>
                         </div>
                       ) : bodyComposition ? (
                         <>
                           <h4 className="plicometry-subtitle mb-15">Analisi Composizione Corporea</h4>
                           <div className={`plicometry-highlight-grid ${isMobile ? 'mobile' : ''} three-cols`} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '15px' }}>
                             
                             {/* Jackson-Pollock 7 */}
                             <div className="highlight-card">
                               <div className="highlight-header">
                                 <div className="highlight-label" style={{ fontSize: '0.9rem' }}>Jackson-Pollock 7</div>
                               </div>
                               {bodyComposition.jacksonPollock7 ? (
                                 <>
                                   <div className="highlight-value" style={{ fontSize: '1.8rem', color: '#ebcd61' }}>
                                     {bodyComposition.jacksonPollock7.bodyFatPercentage.toFixed(1)}%
                                   </div>
                                   <div className="highlight-details" style={{ fontSize: '0.85rem', marginTop: '8px', color: 'rgba(255,255,255,0.7)' }}>
                                     <div>Grassa: {bodyComposition.jacksonPollock7.fatMass !== null ? `${bodyComposition.jacksonPollock7.fatMass.toFixed(1)} kg` : 'N/D'}</div>
                                     <div>Magra: {bodyComposition.jacksonPollock7.leanMass !== null ? `${bodyComposition.jacksonPollock7.leanMass.toFixed(1)} kg` : 'N/D'}</div>
                                   </div>
                                 </>
                               ) : (
                                 <div className="highlight-empty" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.3)', marginTop: '10px' }}>Dati insufficienti (7 pliche)</div>
                               )}
                             </div>

                             {/* Jackson-Pollock 3 */}
                             <div className="highlight-card">
                               <div className="highlight-header">
                                 <div className="highlight-label" style={{ fontSize: '0.9rem' }}>Jackson-Pollock 3</div>
                               </div>
                               {bodyComposition.jacksonPollock3 ? (
                                 <>
                                   <div className="highlight-value" style={{ fontSize: '1.8rem', color: '#ebcd61' }}>
                                     {bodyComposition.jacksonPollock3.bodyFatPercentage.toFixed(1)}%
                                   </div>
                                   <div className="highlight-details" style={{ fontSize: '0.85rem', marginTop: '8px', color: 'rgba(255,255,255,0.7)' }}>
                                     <div>Grassa: {bodyComposition.jacksonPollock3.fatMass !== null ? `${bodyComposition.jacksonPollock3.fatMass.toFixed(1)} kg` : 'N/D'}</div>
                                     <div>Magra: {bodyComposition.jacksonPollock3.leanMass !== null ? `${bodyComposition.jacksonPollock3.leanMass.toFixed(1)} kg` : 'N/D'}</div>
                                   </div>
                                 </>
                               ) : (
                                 <div className="highlight-empty" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.3)', marginTop: '10px' }}>Dati insufficienti (3 pliche)</div>
                               )}
                             </div>

                             {/* Durnin-Womersley */}
                             <div className="highlight-card">
                               <div className="highlight-header">
                                 <div className="highlight-label" style={{ fontSize: '0.9rem' }}>Durnin-Womersley</div>
                               </div>
                               {bodyComposition.durninWomersley ? (
                                 <>
                                   <div className="highlight-value" style={{ fontSize: '1.8rem', color: '#ebcd61' }}>
                                     {bodyComposition.durninWomersley.bodyFatPercentage.toFixed(1)}%
                                   </div>
                                   <div className="highlight-details" style={{ fontSize: '0.85rem', marginTop: '8px', color: 'rgba(255,255,255,0.7)' }}>
                                     <div>Grassa: {bodyComposition.durninWomersley.fatMass !== null ? `${bodyComposition.durninWomersley.fatMass.toFixed(1)} kg` : 'N/D'}</div>
                                     <div>Magra: {bodyComposition.durninWomersley.leanMass !== null ? `${bodyComposition.durninWomersley.leanMass.toFixed(1)} kg` : 'N/D'}</div>
                                   </div>
                                 </>
                               ) : (
                                 <div className="highlight-empty" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.3)', marginTop: '10px' }}>Dati insufficienti (4 pliche)</div>
                               )}
                             </div>

                           </div>
                         </>
                       ) : null}
                     </div>
                   )}

                   {/* Ultima plicometria */}
                   {latestMeasurement ? (
                   <div className="plicometry-latest">
                       <div className="plicometry-latest-header">
                         <div>
                           <div className="plicometry-date-header">
                             {new Date(latestMeasurement.measurement_date).toLocaleDateString('it-IT', {
                               day: '2-digit',
                               month: 'long',
                               year: 'numeric'
                             })}
                    </div>
                           {latestMeasurement.note && (
                             <div className="measurement-note">
                               {latestMeasurement.note}
                    </div>
                 )}
                         </div>
                       </div>
                   <div className={`measurement-grid ${isMobile ? 'mobile' : ''}`}>
                         {measurementFields.map((field) => (
                           latestMeasurement[field.key] !== null &&
                           latestMeasurement[field.key] !== undefined && (
                             <div key={field.key} className="measurement-card">
                     <div className="measurement-label">
                                 {field.label}
                     </div>
                     <div className="measurement-value">
                                 {parseFloat(latestMeasurement[field.key]).toFixed(2)} mm
                     </div>
                   </div>
                           )
                         ))}
                       </div>
                     </div>
                   ) : (
                   <div className="empty-state-large">
                       <p className="empty-state-text">
                         Nessuna plicometria registrata
                       </p>
                       <button
                         onClick={() => setShowAddDialog(true)}
                         className="plicometry-button-primary"
                         style={{ display: 'inline-flex', alignItems: 'center' }}
                       >
                         <MdAdd className="icon-medium" />
                         <span>Aggiungi Prima Plicometria</span>
                       </button>
                  </div>
                )}
                  </div>
                )}

              {/* Dialog Aggiungi Nuova Plicometria */}
              {showAddDialog && (
                <div
                  className="modal-overlay"
                  onClick={(e) => {
                    if (e.target === e.currentTarget) {
                      setShowAddDialog(false);
                    }
                  }}
                >
                  <div className="modal-content" style={{ maxWidth: '800px' }}>
                    <div className="modal-header">
                      <h2 className="modal-title">
                        Aggiungi Nuova Plicometria
                      </h2>
                      <button
                        onClick={() => setShowAddDialog(false)}
                        className="modal-close"
                      >
                        <MdClose />
                      </button>
                    </div>

                    <form onSubmit={handleAddMeasurement}>
                      <div className={`form-grid ${isMobile ? 'mobile' : ''} mb-20`}>
                        <div>
                          <label className="label">
                            Data misurazione
                          </label>
                          <input
                            type="datetime-local"
                            name="measurement_date"
                            value={measurementForm.measurement_date}
                            onChange={handleMeasurementChange}
                            className="input-field"
                          />
                     </div>
                         <div>
                           <label className="label">
                             Note
                           </label>
                           <textarea
                             name="note"
                             value={measurementForm.note}
                             onChange={handleMeasurementChange}
                             rows={isMobile ? 3 : 2}
                             placeholder="Aggiungi eventuali note"
                             className="textarea-field"
                           />
                   </div>
                       </div>
                       
                       <div className="mb-20">
                          <label className="label">
                            Peso (kg) - Aggiorna peso cliente
                          </label>
                          <input
                            type="number"
                            name="peso"
                            value={measurementForm.peso}
                            onChange={handleMeasurementChange}
                            step="0.1"
                            className="input-field"
                            placeholder={selectedClient?.peso ? `Attuale: ${selectedClient.peso}` : "Inserisci peso attuale"}
                          />
                       </div>

                   <div className={`form-grid-auto ${isMobile ? 'mobile' : ''} mb-20`}>
                        {measurementFields.map((field) => (
                          <div key={field.key}>
                            <label className="label">
                              {field.label}
                            </label>
                            <input
                              type="number"
                              name={field.key}
                              value={measurementForm[field.key]}
                              onChange={handleMeasurementChange}
                              step="0.01"
                              placeholder="0.00"
                              className="input-field"
                            />
                          </div>
                        ))}
                      </div>

                      <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--text-primary)' }}>
                        Circonferenze (cm)
                      </h3>
                      <div className={`form-grid-auto ${isMobile ? 'mobile' : ''} mb-20`}>
                        {circumferenceFields.map((field) => (
                          <div key={field.key}>
                            <label className="label">
                              {field.label}
                            </label>
                            <input
                              type="number"
                              name={field.key}
                              value={measurementForm[field.key]}
                              onChange={handleMeasurementChange}
                              step="0.01"
                              placeholder="0.00"
                              className="input-field"
                            />
                          </div>
                        ))}
                      </div>

                       <button
                         type="submit"
                         className="btn-primary"
                         disabled={isSubmittingMeasurement}
                       >
                         {isSubmittingMeasurement ? (
                           <>
                             <div className="loading-spinner-small" style={{ marginRight: '8px', display: 'inline-block', width: '16px', height: '16px' }}></div>
                             Salvataggio...
                           </>
                         ) : (
                           'Salva Plicometria'
                         )}
                       </button>

                      {measurementMessage && (
                  <div style={{
                          marginTop: '16px',
                          padding: '12px',
                    borderRadius: '8px',
                          backgroundColor: measurementMessage.includes('successo')
                            ? 'rgba(40, 167, 69, 0.15)'
                            : 'rgba(220, 53, 69, 0.15)',
                          border: `1px solid ${measurementMessage.includes('successo')
                            ? 'rgba(40, 167, 69, 0.3)'
                            : 'rgba(220, 53, 69, 0.3)'}`,
                          color: measurementMessage.includes('successo') ? '#4ade80' : '#ff6b6b',
                          fontSize: '14px'
                        }}>
                          {measurementMessage}
                    </div>
                      )}
                    </form>
                    </div>
                  </div>
                )}

              {/* Dialog Registro Storico Plicometrie */}
              {showHistoryDialog && (
                <div
                  className="modal-overlay"
                  onClick={(e) => {
                    if (e.target === e.currentTarget) {
                      setShowHistoryDialog(false);
                    }
                  }}
                >
                  <div className="modal-content" style={{ maxWidth: '900px' }}>
                    <div className="modal-header">
                      <h2 className="modal-title">
                        Registro Plicometrie
                      </h2>
                      <button
                        onClick={() => setShowHistoryDialog(false)}
                        className="modal-close"
                      >
                        <MdClose />
                      </button>
                    </div>

                    {measurementsLoading ? (
                      <div className="loading-text">
                        Caricamento storico plicometrie...
                      </div>
                    ) : measurementsError ? (
                      <div className="message error">
                        {measurementsError}
                     </div>
                     ) : measurements.length === 0 ? (
                       <div className="empty-state">
                         Nessuna plicometria registrata
                     </div>
                     ) : (
                       <div className="history-list">
                         {measurements.map((measurement) => (
                           <div key={measurement.id} className="history-item">
                             <div className={`history-item-header ${isMobile ? 'mobile' : ''}`}>
                               <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                 <div>
                                   <strong className="history-date">
                                     {new Date(measurement.measurement_date).toLocaleString('it-IT', {
                                       day: '2-digit',
                                       month: 'long',
                                       year: 'numeric',
                                       hour: '2-digit',
                                       minute: '2-digit'
                                     })}
                                   </strong>
                                 </div>
                                 <button
                                    onClick={() => handleDeleteMeasurement(measurement.id)}
                                    title="Elimina misurazione"
                                    style={{
                                      padding: '6px 10px',
                                      marginLeft: '12px',
                                      background: 'rgba(244, 67, 54, 0.1)',
                                      border: '1px solid rgba(244, 67, 54, 0.3)',
                                      color: '#f44336',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      transition: 'all 0.2s ease',
                                      fontSize: '16px',
                                      minWidth: '36px',
                                      height: '36px'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = 'rgba(244, 67, 54, 0.2)';
                                      e.currentTarget.style.borderColor = 'rgba(244, 67, 54, 0.5)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = 'rgba(244, 67, 54, 0.1)';
                                      e.currentTarget.style.borderColor = 'rgba(244, 67, 54, 0.3)';
                                    }}
                                  >
                                    <MdDelete size={18} />
                                  </button>
                               </div>
                               {measurement.note && (
                                 <div className="history-note">
                                   {measurement.note}
                   </div>
                 )}
                             </div>
                   <div className={`history-measurements-grid ${isMobile ? 'mobile' : ''}`}>
                              {measurementFields.map((field) => (
                                measurement[field.key] !== null &&
                                measurement[field.key] !== undefined && (
                                  <div key={field.key} className="measurement-card">
                                    <div className="measurement-label">
                                      {field.label}
                                    </div>
                                    <div className="measurement-value">
                                      {parseFloat(measurement[field.key]).toFixed(2)} mm
                                    </div>
                                  </div>
                                )
                              ))}
                            </div>

                            {/* Sezione Circonferenze nello storico */}
                            {circumferenceFields.some(field => measurement[field.key] !== null && measurement[field.key] !== undefined) && (
                              <>
                                <div style={{ 
                                  fontSize: '0.9rem', 
                                  fontWeight: '600', 
                                  marginTop: '16px', 
                                  marginBottom: '8px', 
                                  color: 'var(--text-secondary)',
                                  borderTop: '1px solid var(--border-color)',
                                  paddingTop: '12px'
                                }}>
                                  Circonferenze
                                </div>
                                <div className={`history-measurements-grid ${isMobile ? 'mobile' : ''}`}>
                                  {circumferenceFields.map((field) => (
                                    measurement[field.key] !== null &&
                                    measurement[field.key] !== undefined && (
                                      <div key={field.key} className="measurement-card" style={{ backgroundColor: 'rgba(59, 130, 246, 0.05)' }}>
                                        <div className="measurement-label">
                                          {field.label}
                                        </div>
                                        <div className="measurement-value">
                                          {parseFloat(measurement[field.key]).toFixed(2)} cm
                                        </div>
                                      </div>
                                    )
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                   </div>
                 )}
                   </div>
               </div>
             )}
            </div>
          </div>
        </>
      )}

      <ConfirmDialog
        isOpen={deleteClientConfirm.isOpen}
        onClose={() => setDeleteClientConfirm({ isOpen: false })}
        onConfirm={handleDeleteClient}
        title="Conferma Eliminazione"
        message={`Sei sicuro di voler eliminare il cliente "${selectedClient.nome} ${selectedClient.cognome}"? Questa azione non può essere annullata e eliminerà anche tutte le schede e le plicometrie associate.`}
        confirmText="Elimina"
        cancelText="Annulla"
        type="danger"
      />

      <ConfirmDialog
        isOpen={deleteMeasurementConfirm.isOpen}
        onClose={() => setDeleteMeasurementConfirm({ isOpen: false, measurementId: null })}
        onConfirm={confirmDeleteMeasurement}
        title="Conferma Eliminazione Misurazione"
        message="Sei sicuro di voler eliminare questa misurazione? Questa azione non può essere annullata."
        confirmText="Elimina"
        cancelText="Annulla"
        type="danger"
      />
    </>
  );
}

// Sezione Macchinari
function MacchinariSection({ machines = [], loading = false, muscleGroups = {}, loadingMuscleGroups = false, error = '', onRefresh }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' o 'table'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  const [showModal, setShowModal] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    descrizione: '',
    gruppiMuscolari: [], // Ora contiene ID numerici
    imageFile: null // Nuovo campo per file immagine
  });
  const [formLoading, setFormLoading] = useState(false);
  const [message, setMessage] = useState(error);
  const [modalExpandedCategories, setModalExpandedCategories] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, machine: null });
  
  useEffect(() => {
    if (error) setMessage(error);
  }, [error]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Carica i dati del macchinario quando si entra in modalità modifica
  useEffect(() => {
    if (editingMachine) {
      setFormData({
        nome: editingMachine.nome || '',
        descrizione: editingMachine.descrizione || '',
        gruppiMuscolari: editingMachine.gruppiMuscolari ? editingMachine.gruppiMuscolari.map(mg => mg.id) : []
      });
      setShowModal(true);
    }
  }, [editingMachine]);

  const allMacchinari = machines;
  const availableMuscleGroups = muscleGroups;

  // Ottieni tutti i gruppi muscolari disponibili (dalla prop muscleGroups)
  // Usa availableMuscleGroups se disponibile, altrimenti estrai dai macchinari
  const allGroups = React.useMemo(() => {
    if (Object.keys(availableMuscleGroups).length > 0) {
      // Usa i gruppi muscolari dalla tabella (raggruppati per macrocategoria)
      const allGroupsList = [];
      Object.values(availableMuscleGroups).forEach(gruppi => {
        gruppi.forEach(gruppo => {
          allGroupsList.push(gruppo.nome);
        });
      });
      return allGroupsList.sort();
    } else {
      // Fallback: estrai dai macchinari esistenti
      return [...new Set(
        allMacchinari.flatMap(m => {
          if (Array.isArray(m.gruppiMuscolari)) {
            return m.gruppiMuscolari.map(mg => typeof mg === 'object' ? mg.nome : mg);
          }
          return [];
        })
      )].sort();
    }
  }, [availableMuscleGroups, allMacchinari]);

  // Filtra i macchinari
  const macchinari = allMacchinari.filter(macchinario => {
    // Filtro per ricerca
    const matchesSearch = searchTerm === '' || 
      macchinario.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      macchinario.descrizione.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filtro per gruppi muscolari (dalla nuova struttura)
    const matchesGroups = selectedGroups.length === 0 ||
      selectedGroups.some(group => {
        if (Array.isArray(macchinario.gruppiMuscolari)) {
          return macchinario.gruppiMuscolari.some(mg => {
            const nome = typeof mg === 'object' ? mg.nome : mg;
            return nome === group;
          });
        }
        return false;
      });
    
    return matchesSearch && matchesGroups;
  });

  const handleGroupToggle = (group) => {
    setSelectedGroups(prev => 
      prev.includes(group) 
        ? prev.filter(g => g !== group)
        : [...prev, group]
    );
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleModalCategory = (category) => {
    setModalExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedGroups([]);
  };

  const hasActiveFilters = searchTerm !== '' || selectedGroups.length > 0;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleToggleMuscleGroup = (group) => {
    setFormData({
      ...formData,
      gruppiMuscolari: formData.gruppiMuscolari.includes(group)
        ? formData.gruppiMuscolari.filter(g => g !== group)
        : [...formData.gruppiMuscolari, group]
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const url = `${API_URL}/api/client/machines`;

      // Usa FormData per supportare file upload
      const dataToSend = new FormData();
      dataToSend.append('nome', formData.nome);
      dataToSend.append('descrizione', formData.descrizione);
      dataToSend.append('gruppiMuscolari', JSON.stringify(formData.gruppiMuscolari));
      
      if (formData.imageFile) {
        dataToSend.append('image', formData.imageFile);
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Non settare Content-Type con FormData
        },
        body: dataToSend
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Macchinario creato con successo!');
        // Reset form
        setFormData({
          nome: '',
          descrizione: '',
          gruppiMuscolari: [],
          imageFile: null
        });
        setEditingMachine(null);
        
        if (onRefresh) onRefresh();
        
        setTimeout(() => {
          setShowModal(false);
          setMessage('');
          // Vai alla pagina dettaglio del nuovo macchinario
          if (data.machine) {
            setSelectedMachine(data.machine);
          }
        }, 1500);
      } else {
        setMessage(data.error || 'Errore durante la creazione del macchinario');
      }
    } catch (error) {
      setMessage('Errore di connessione al server');
    } finally {
      setFormLoading(false);
    }
  };

  // handleEdit rimosso - non più utilizzato, si usa setSelectedMachine direttamente

  const handleDelete = async (macchinario) => {
    setDeleteConfirm({ isOpen: true, machine: macchinario });
  };

  const confirmDelete = async () => {
    const macchinario = deleteConfirm.machine;
    if (!macchinario) return;

    setDeleteConfirm({ isOpen: false, machine: null });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/machines/${macchinario.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Macchinario eliminato con successo!');
        if (onRefresh) onRefresh();
        
        setTimeout(() => {
          setMessage('');
        }, 1500);
      } else {
        setMessage(data.error || 'Errore durante l\'eliminazione del macchinario');
      }
    } catch (error) {
      setMessage('Errore di connessione al server');
    }
  };

  // Se un macchinario è selezionato, mostra i dettagli
  if (selectedMachine) {
    return (
      <DettagliMacchinarioSection
        macchinario={selectedMachine}
        onBack={() => setSelectedMachine(null)}
        onEdit={(macchinario) => {
          // Non serve più aprire il modal, la modifica è inline
        }}
        onDelete={(macchinario) => {
          handleDelete(macchinario);
          setSelectedMachine(null);
        }}
        onUpdate={(updatedMachine) => {
          if (onRefresh) onRefresh();
          setSelectedMachine(updatedMachine);
        }}
      />
    );
  }

  return (
    <>
      {/* Header con titolo, bottone aggiungi e toggle visualizzazione */}
      <div className={`machines-header ${isMobile ? 'mobile' : ''}`}>
      <h1 className={`section-title ${isMobile ? 'mobile' : ''}`}>
        Macchinari
        </h1>
        
        <div className="machines-actions">
          {/* Bottone Aggiungi Macchinario */}
          <button
            onClick={() => setShowModal(true)}
            className="btn-primary button-with-icon"
          >
            <MdAdd className="icon-large" />
            <span>Aggiungi</span>
          </button>
          
          {/* Toggle visualizzazione */}
          <div className="machines-view-toggle">
            <button
              onClick={() => setViewMode('cards')}
              className={`machines-view-button ${viewMode === 'cards' ? 'active' : ''}`}
            >
              Card
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`machines-view-button ${viewMode === 'table' ? 'active' : ''}`}
            >
              Tabella
            </button>
          </div>
        </div>
      </div>

      {/* Barra di ricerca e filtri */}
      <div className="machines-search-container">
        {/* Barra di ricerca */}
        <div className="machines-search-wrapper">
          <MdSearch className="search-icon" />
          <input
            type="text"
            placeholder="Cerca macchinari"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field input-search"
          />
        </div>

        {/* Filtri gruppi muscolari */}
        <div className="machines-filters">
          <div className="machines-filters-header">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="filter-toggle-button"
            >
              <span>Filtra per Gruppi Muscolari</span>
              <span className={`filter-arrow ${showFilters ? 'open' : ''}`}>
                ▼
              </span>
            </button>

            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="reset-filters-button"
              >
                <MdClear className="icon-medium" />
                <span>Reset Filtri</span>
              </button>
            )}
          </div>

          {showFilters && (
            <div className="machines-filters-content">
              {Object.keys(availableMuscleGroups).length > 0 ? (
                // Mostra raggruppati per macrocategoria con accordion
                <div className="muscle-groups-accordion">
                  {Object.entries(availableMuscleGroups).map(([macrocategoria, gruppi]) => {
                    const isExpanded = expandedCategories[macrocategoria] === true; // Default chiuso
                    const selectedCount = gruppi.filter(g => selectedGroups.includes(g.nome)).length;
                    
                    return (
                      <div key={macrocategoria} className="muscle-category-card">
                        <button
                          type="button"
                          className="muscle-category-header"
                          onClick={() => toggleCategory(macrocategoria)}
                        >
                          <div className="muscle-category-title-wrapper">
                            <span className="muscle-category-title">{macrocategoria}</span>
                            {selectedCount > 0 && (
                              <span className="muscle-category-badge">{selectedCount}</span>
                            )}
                          </div>
                          <span className={`muscle-category-arrow ${isExpanded ? 'expanded' : ''}`}>
                            ▼
                          </span>
                        </button>
                        {isExpanded && (
                          <div className="muscle-groups-grid">
                            {gruppi.map((gruppo) => (
                              <label
                                key={gruppo.id}
                                className={`muscle-group-checkbox-label ${selectedGroups.includes(gruppo.nome) ? 'checked' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedGroups.includes(gruppo.nome)}
                                  onChange={() => handleGroupToggle(gruppo.nome)}
                                  className="muscle-group-checkbox"
                                />
                                <span className="muscle-group-name">{gruppo.nome}</span>
                              </label>
                            ))}
                          </div>
                        )}
              </div>
            );
          })}
                </div>
              ) : allGroups.length > 0 ? (
                // Fallback: lista piatta se non ci sono macrocategorie
                <div className="muscle-groups-grid">
                  {allGroups.map((group) => (
                    <label
                      key={group}
                      className={`muscle-group-checkbox-label ${selectedGroups.includes(group) ? 'checked' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedGroups.includes(group)}
                        onChange={() => handleGroupToggle(group)}
                        className="muscle-group-checkbox"
                      />
                      <span className="muscle-group-name">{group}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="muscle-groups-empty">
                  Nessun gruppo muscolare disponibile
                </div>
              )}
              {selectedGroups.length > 0 && (
                <div className="muscle-filters-summary">
                  <span className="muscle-filters-count">
                    {selectedGroups.length} {selectedGroups.length === 1 ? 'filtro attivo' : 'filtri attivi'}
                  </span>
                </div>
              )}
            </div>
          )}
      </div>

        {/* Indicatore risultati */}
        {hasActiveFilters && (
          <div className="machines-results">
            <span className="machines-results-text">
              {macchinari.length} macchinari{macchinari.length !== 1 ? 'o' : ''} trovati
            </span>
          </div>
        )}
      </div>

      {/* Messaggio di errore */}
      {message && (
        <div style={{
          padding: '12px 20px',
          marginBottom: '20px',
          backgroundColor: message.includes('successo') 
            ? 'rgba(76, 175, 80, 0.2)' 
            : 'rgba(244, 67, 54, 0.2)',
          border: `1px solid ${message.includes('successo') 
            ? 'rgba(76, 175, 80, 0.4)' 
            : 'rgba(244, 67, 54, 0.4)'}`,
          borderRadius: '8px',
          color: message.includes('successo') ? '#4caf50' : '#f44336',
          fontSize: '14px'
        }}>
          {message}
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '60px 20px',
          color: 'rgba(255, 255, 255, 0.5)',
          fontSize: '16px' 
        }}>
          Caricamento macchinari...
        </div>
      ) : macchinari.length === 0 ? (
        /* Nessun macchinario trovato */
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: 'rgba(255, 255, 255, 0.5)',
          fontSize: '16px'
        }}>
          {allMacchinari.length === 0 
            ? 'Nessun macchinario disponibile. Aggiungi il primo macchinario!'
            : 'Nessun macchinario corrisponde ai filtri selezionati.'}
        </div>
      ) : viewMode === 'cards' ? (
        /* Visualizzazione Card */
        <div className={`machines-cards-grid ${isMobile ? 'mobile' : ''}`}>
          {macchinari.map((macchinario) => (
            <div
              key={macchinario.id}
              className="card machine-card-hover"
              onClick={() => setSelectedMachine(macchinario)}
              style={{ cursor: 'pointer' }}
            >
              <h3 className="machine-card-title" style={{ margin: 0, marginBottom: '12px' }}>
                {macchinario.nome}
              </h3>
      <p className="machine-card-description">
                {macchinario.descrizione}
              </p>
              <div className="machine-card-divider">
                <div className="machine-groups-label">
                  Gruppi Muscolari
      </div>
                <div className="machine-groups-list">
                  {macchinario.gruppiMuscolari && macchinario.gruppiMuscolari.slice(0, 3).map((gruppo, index) => {
                    const nome = typeof gruppo === 'object' ? gruppo.nome : gruppo;
                    const macrocategoria = typeof gruppo === 'object' ? gruppo.macrocategoria : null;
                    return (
                      <span
                        key={typeof gruppo === 'object' ? gruppo.id : index}
                        className="badge machines-badge-large"
                        title={macrocategoria ? `Macrocategoria: ${macrocategoria}` : ''}
                      >
                        {nome}
                      </span>
                    );
                  })}
                  {macchinario.gruppiMuscolari && macchinario.gruppiMuscolari.length > 3 && (
                    <span className="badge machines-badge-large" style={{ opacity: 0.7 }} title="Clicca per vedere tutti i gruppi">
                      +{macchinario.gruppiMuscolari.length - 3}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Visualizzazione Tabella */
        <div className={`machines-table-wrapper ${isMobile ? 'mobile' : ''}`}>
          <table className="machines-table">
            <thead>
              <tr className="machines-table-header">
                <th>Nome</th>
                <th>Descrizione</th>
                <th>Gruppi Muscolari</th>
              </tr>
            </thead>
            <tbody>
              {macchinari.length === 0 ? (
                <tr>
                  <td colSpan="4" style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: 'rgba(255, 255, 255, 0.5)',
                    fontSize: '14px'
                  }}>
                    {allMacchinari.length === 0 
                      ? 'Nessun macchinario disponibile. Aggiungi il primo macchinario!'
                      : 'Nessun macchinario corrisponde ai filtri selezionati.'}
                  </td>
                </tr>
              ) : (
                macchinari.map((macchinario, index) => (
                <tr
                  key={macchinario.id}
                  className="machines-table-row"
                  onClick={() => setSelectedMachine(macchinario)}
                  style={{
                    borderBottom: index < macchinari.length - 1 ? '1px solid rgba(235, 205, 97, 0.1)' : 'none',
                    cursor: 'pointer'
                  }}
                >
                  <td className="machines-table-cell machines-table-cell-name">
                    {macchinario.nome}
                  </td>
                  <td className="machines-table-cell machines-table-cell-desc">
                    {macchinario.descrizione}
                  </td>
                  <td className="machines-table-cell">
                    <div className="machines-badges">
                      {macchinario.gruppiMuscolari && macchinario.gruppiMuscolari.map((gruppo, idx) => {
                        const nome = typeof gruppo === 'object' ? gruppo.nome : gruppo;
                        const macrocategoria = typeof gruppo === 'object' ? gruppo.macrocategoria : null;
                        return (
                          <span
                            key={typeof gruppo === 'object' ? gruppo.id : idx}
                            className="machines-badge"
                            title={macrocategoria ? `Macrocategoria: ${macrocategoria}` : ''}
                          >
                            {nome}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal per aggiungere macchinario */}
      {showModal && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowModal(false);
              setEditingMachine(null);
              setFormData({
                nome: '',
                descrizione: '',
                gruppiMuscolari: []
              });
            }
          }}
        >
          <div className="modal-content" style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2 className="modal-title">
                Aggiungi Nuovo Macchinario
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingMachine(null);
                  setFormData({
                    nome: '',
                    descrizione: '',
                    gruppiMuscolari: []
                  });
                  setMessage('');
                }}
                className="modal-close"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-20">
                <label className="label">Nome *</label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  required
                  className="input-field"
                  placeholder="Nome del macchinario"
                />
              </div>

              <div className="mb-20">
                <label className="label">Descrizione</label>
                <textarea
                  name="descrizione"
                  value={formData.descrizione}
                  onChange={handleChange}
                  rows={isMobile ? 4 : 3}
                  className="textarea-field"
                  placeholder="Descrizione del macchinario"
                />
              </div>

              {/* Input File Immagine */}
              <div className="mb-20">
                <label className="label">Foto Macchinario (Opzionale)</label>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    flexWrap: 'wrap'
                  }}>
                    <label
                      htmlFor="machine-image-upload"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 20px',
                        background: 'linear-gradient(135deg, rgba(235, 205, 97, 0.2) 0%, rgba(235, 205, 97, 0.15) 100%)',
                        border: '1px solid rgba(235, 205, 97, 0.4)',
                        borderRadius: '8px',
                        color: '#ebcd61',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        transition: 'all 0.3s ease',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(235, 205, 97, 0.3) 0%, rgba(235, 205, 97, 0.25) 100%)';
                        e.currentTarget.style.borderColor = 'rgba(235, 205, 97, 0.6)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(235, 205, 97, 0.2) 0%, rgba(235, 205, 97, 0.15) 100%)';
                        e.currentTarget.style.borderColor = 'rgba(235, 205, 97, 0.4)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <MdPhotoCamera size={20} />
                      <span>Scegli File</span>
                    </label>
                    <input
                      id="machine-image-upload"
                      type="file"
                      accept="image/*"
                      onChange={(e) => setFormData({ ...formData, imageFile: e.target.files[0] })}
                      style={{ display: 'none' }}
                    />
                    {formData.imageFile && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 16px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        flex: 1,
                        minWidth: '200px'
                      }}>
                        <span style={{
                          color: 'rgba(255, 255, 255, 0.9)',
                          fontSize: '14px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1
                        }}>
                          {formData.imageFile.name}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setFormData({ ...formData, imageFile: null });
                            const input = document.getElementById('machine-image-upload');
                            if (input) input.value = '';
                          }}
                          style={{
                            background: 'rgba(244, 67, 54, 0.2)',
                            border: '1px solid rgba(244, 67, 54, 0.4)',
                            borderRadius: '6px',
                            color: '#f44336',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(244, 67, 54, 0.3)';
                            e.currentTarget.style.borderColor = 'rgba(244, 67, 54, 0.6)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(244, 67, 54, 0.2)';
                            e.currentTarget.style.borderColor = 'rgba(244, 67, 54, 0.4)';
                          }}
                          title="Rimuovi file"
                        >
                          <MdClose size={16} />
                        </button>
                      </div>
                    )}
                    {!formData.imageFile && (
                      <span style={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontSize: '14px',
                        fontStyle: 'italic'
                      }}>
                        Nessun file selezionato
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mb-20">
                <label className="label">Gruppi Muscolari</label>
                {loadingMuscleGroups ? (
                  <div style={{ 
                    padding: '20px', 
                    textAlign: 'center', 
                    color: 'rgba(255, 255, 255, 0.5)' 
                  }}>
                    Caricamento gruppi muscolari...
                  </div>
                ) : Object.keys(availableMuscleGroups).length === 0 ? (
                  <div style={{ 
                    padding: '20px', 
                    textAlign: 'center', 
                    color: 'rgba(255, 255, 255, 0.5)' 
                  }}>
                    Nessun gruppo muscolare disponibile nel database
                  </div>
                ) : (
                  <div className="modal-muscle-groups-container">
                    <div className="muscle-groups-accordion">
                      {Object.entries(availableMuscleGroups).map(([macrocategoria, gruppi]) => {
                        const isExpanded = modalExpandedCategories[macrocategoria] === true; // Default chiuso
                        const selectedCount = gruppi.filter(g => formData.gruppiMuscolari.includes(g.id)).length;
                        
                        return (
                          <div key={macrocategoria} className="muscle-category-card">
                            <button
                              type="button"
                              className="muscle-category-header"
                              onClick={() => toggleModalCategory(macrocategoria)}
                            >
                              <div className="muscle-category-title-wrapper">
                                <span className="muscle-category-title">{macrocategoria}</span>
                                {selectedCount > 0 && (
                                  <span className="muscle-category-badge">{selectedCount}</span>
                                )}
                              </div>
                              <span className={`muscle-category-arrow ${isExpanded ? 'expanded' : ''}`}>
                                ▼
                              </span>
                            </button>
                            {isExpanded && (
                              <div className="muscle-groups-grid">
                                {gruppi.map((gruppo) => (
                                  <label
                                    key={gruppo.id}
                                    className={`muscle-group-checkbox-label ${formData.gruppiMuscolari.includes(gruppo.id) ? 'checked' : ''}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={formData.gruppiMuscolari.includes(gruppo.id)}
                                      onChange={() => handleToggleMuscleGroup(gruppo.id)}
                                      className="muscle-group-checkbox"
                                    />
                                    <span className="muscle-group-name">{gruppo.nome}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {formData.gruppiMuscolari.length > 0 && (
                      <div className="muscle-filters-summary">
                        <span className="muscle-filters-count">
                          {formData.gruppiMuscolari.length} {formData.gruppiMuscolari.length === 1 ? 'gruppo selezionato' : 'gruppi selezionati'}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                {formData.gruppiMuscolari.length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ 
                      fontSize: '12px', 
                      color: 'rgba(255, 255, 255, 0.6)',
                      marginBottom: '8px'
                    }}>
                      Selezionati: {formData.gruppiMuscolari.length}
                    </div>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px'
                    }}>
                      {formData.gruppiMuscolari.map((groupId) => {
                        // Trova il gruppo muscolare corrispondente
                        let gruppo = null;
                        for (const [macrocategoria, gruppi] of Object.entries(availableMuscleGroups)) {
                          const found = gruppi.find(g => g.id === groupId);
                          if (found) {
                            gruppo = { ...found, macrocategoria };
                            break;
                          }
                        }
                        
                        if (!gruppo) return null;
                        
                        return (
                          <span
                            key={groupId}
                            style={{
                              backgroundColor: 'rgba(235, 205, 97, 0.2)',
          color: '#ebcd61', 
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                            title={gruppo.macrocategoria}
                          >
                            {gruppo.nome}
                            <button
                              type="button"
                              onClick={() => handleToggleMuscleGroup(groupId)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#ebcd61',
                                cursor: 'pointer',
                                fontSize: '14px',
                                padding: 0,
                                lineHeight: 1
                              }}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {message && (
                <div style={{
                  padding: '12px',
          marginBottom: '20px',
                  backgroundColor: message.includes('successo') 
                    ? 'rgba(76, 175, 80, 0.2)' 
                    : 'rgba(244, 67, 54, 0.2)',
                  border: `1px solid ${message.includes('successo') 
                    ? 'rgba(76, 175, 80, 0.4)' 
                    : 'rgba(244, 67, 54, 0.4)'}`,
                  borderRadius: '8px',
                  color: message.includes('successo') ? '#4caf50' : '#f44336',
                  fontSize: '14px'
                }}>
                  {message}
                </div>
              )}

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setFormData({
                      nome: '',
                      descrizione: '',
                      gruppiMuscolari: []
                    });
                    setMessage('');
                  }}
                  className="btn-secondary"
                  disabled={formLoading}
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={formLoading}
                >
                  {formLoading ? 'Creazione...' : 'Crea Macchinario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, machine: null })}
        onConfirm={confirmDelete}
        title="Conferma Eliminazione"
        message={`Sei sicuro di voler eliminare il macchinario "${deleteConfirm.machine?.nome}"? Questa azione non può essere annullata.`}
        confirmText="Elimina"
        cancelText="Annulla"
        type="danger"
      />
    </>
  );
}

// Sezione Dettagli Macchinario
function DettagliMacchinarioSection({ macchinario, onBack, onEdit, onDelete, onUpdate }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [availableMuscleGroups, setAvailableMuscleGroups] = useState({});
  const [loadingMuscleGroups, setLoadingMuscleGroups] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [currentMachine, setCurrentMachine] = useState(macchinario);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setCurrentMachine(macchinario);
  }, [macchinario]);

  useEffect(() => {
    if (isEditing) {
      loadMuscleGroups();
      // Estrai gli ID dai gruppi muscolari
      const muscleGroupIds = currentMachine.gruppiMuscolari 
        ? currentMachine.gruppiMuscolari.map(mg => typeof mg === 'object' ? mg.id : mg)
        : [];
      
      setEditData({
        nome: currentMachine.nome || '',
        descrizione: currentMachine.descrizione || '',
        gruppiMuscolari: Array.isArray(muscleGroupIds) ? muscleGroupIds : []
      });
    } else {
      // Reset editData quando si esce dalla modalità modifica
      setEditData({});
    }
  }, [isEditing, currentMachine]);

  const loadMuscleGroups = async () => {
    setLoadingMuscleGroups(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/machines/groups`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (response.ok) {
        setAvailableMuscleGroups(data.muscleGroups || {});
      } else {
        console.error('Errore durante il caricamento dei gruppi muscolari:', data.error);
        setAvailableMuscleGroups({});
      }
    } catch (error) {
      console.error('Errore di connessione al server:', error);
    } finally {
      setLoadingMuscleGroups(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setMessage('');
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditData({});
    setMessage('');
  };

  const handleChange = (e) => {
    setEditData({
      ...editData,
      [e.target.name]: e.target.value
    });
  };

  const handleToggleMuscleGroup = (groupId) => {
    const currentGroups = Array.isArray(editData.gruppiMuscolari) ? editData.gruppiMuscolari : [];
    setEditData({
      ...editData,
      gruppiMuscolari: currentGroups.includes(groupId)
        ? currentGroups.filter(g => g !== groupId)
        : [...currentGroups, groupId]
    });
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      
      // Usa FormData per supportare file upload
      const formData = new FormData();
      formData.append('nome', editData.nome);
      formData.append('descrizione', editData.descrizione || '');
      formData.append('gruppiMuscolari', JSON.stringify(editData.gruppiMuscolari));
      
      if (editData.imageFile) {
        formData.append('image', editData.imageFile);
      }

      const response = await fetch(`${API_URL}/api/client/machines/${currentMachine.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
          // Non settare Content-Type con FormData
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Macchinario aggiornato con successo!');
        const updatedMachine = data.machine || currentMachine;
        setCurrentMachine(updatedMachine);
        if (onUpdate) {
          onUpdate(updatedMachine);
        }
        setTimeout(() => {
          setIsEditing(false);
          // Rimuovi il file temporaneo dallo stato
          const { imageFile, ...rest } = editData;
          setEditData(rest);
          setMessage('');
        }, 1500);
      } else {
        setMessage(data.error || 'Errore durante l\'aggiornamento del macchinario');
      }
    } catch (error) {
      setMessage('Errore di connessione al server');
    } finally {
      setEditLoading(false);
    }
  };



  if (!currentMachine) {
    return null;
  }

  return (
    <>
      {/* Header con bottone indietro */}
      <div className="detail-header">
        <button
          onClick={onBack}
          className="back-button"
        >
          <MdArrowBack />
        </button>
        <h1 className={`detail-title ${isMobile ? 'mobile' : ''}`}>
          {isEditing ? 'Modifica Macchinario' : currentMachine.nome}
        </h1>
        {!isEditing && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
            <button
              onClick={handleEdit}
              className="edit-button"
            >
              <MdEdit className="icon-medium" />
              <span>Modifica</span>
            </button>
          <button
            onClick={() => {
              setDeleteConfirm({ isOpen: true });
            }}
            style={{
              padding: '10px 20px',
              fontSize: '15px',
              fontWeight: '600',
              background: 'rgba(244, 67, 54, 0.2)',
              color: '#f44336',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(244, 67, 54, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(244, 67, 54, 0.2)';
            }}
          >
            <MdDelete className="icon-medium" />
            <span>Elimina</span>
          </button>
          </div>
        )}
        {isEditing && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
            <button
              onClick={handleCancelEdit}
              className="btn-cancel"
              disabled={editLoading}
            >
              Annulla
            </button>
            <button
              onClick={handleUpdate}
              className="btn-primary"
              disabled={editLoading}
            >
              {editLoading ? 'Salvataggio...' : 'Salva Modifiche'}
            </button>
          </div>
        )}
      </div>

      {/* Messaggio di feedback */}
      {message && (
        <div className={`message ${message.includes('successo') ? 'success' : 'error'}`} style={{ marginBottom: '20px' }}>
          {message}
        </div>
      )}

      {isEditing ? (
        /* Modalità Modifica */
        <form onSubmit={handleUpdate}>
          <div className={`two-column-layout ${isMobile ? 'mobile' : ''}`}>
            {/* Informazioni generali - Modifica */}
            <div className="info-box">
              <h3 className="info-box-title">
                Informazioni Generali
              </h3>
              
              {/* Sezione Immagine - Modifica */}
              <div style={{ marginBottom: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ 
                  width: '100%', 
                  height: '300px', 
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px',
                  overflow: 'hidden',
                  border: '1px solid rgba(235, 205, 97, 0.2)'
                }}>
                  {editData.imageFile ? (
                    <img 
                      src={URL.createObjectURL(editData.imageFile)} 
                      alt="Preview" 
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  ) : currentMachine.image_url ? (
                    <img 
                      src={`${API_URL}${currentMachine.image_url}`} 
                      alt={currentMachine.nome} 
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'rgba(255, 255, 255, 0.3)' }}>
                      <MdFitnessCenter style={{ fontSize: '48px', marginBottom: '8px' }} />
                      <span>Nessuna immagine</span>
                    </div>
                  )}
                  {/* Fallback per errore caricamento immagine */}
                  <div style={{ display: 'none', flexDirection: 'column', alignItems: 'center', color: 'rgba(255, 255, 255, 0.3)' }}>
                    <MdFitnessCenter style={{ fontSize: '48px', marginBottom: '8px' }} />
                    <span>Errore caricamento</span>
                  </div>
                </div>
                
                <label className="btn-secondary" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  <MdPhotoCamera />
                  <span>{currentMachine.image_url ? 'Cambia Foto' : 'Carica Foto'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setEditData({ ...editData, imageFile: e.target.files[0] })}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label className="label">Nome *</label>
                  <input
                    type="text"
                    name="nome"
                    value={editData.nome}
                    onChange={handleChange}
                    required
                    className="input-field"
                    placeholder="Nome del macchinario"
                  />
                </div>
                <div>
                  <label className="label">Descrizione</label>
                  <textarea
                    name="descrizione"
                    value={editData.descrizione}
                    onChange={handleChange}
                    rows={isMobile ? 4 : 3}
                    className="textarea-field"
                    placeholder="Descrizione del macchinario"
                  />
                </div>
                {currentMachine.created_at && (
                  <div className="info-item">
                    <MdCalendarToday className="info-icon" />
                    <span className="info-text">
                      <strong>Creato il:</strong> {new Date(currentMachine.created_at).toLocaleDateString('it-IT', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Gruppi Muscolari - Modifica */}
            <div className="info-box">
              <h3 className="info-box-title">
                Gruppi Muscolari
              </h3>
              {loadingMuscleGroups ? (
                <div style={{ 
                  padding: '20px', 
                  textAlign: 'center', 
                  color: 'rgba(255, 255, 255, 0.5)' 
                }}>
                  Caricamento gruppi muscolari...
                </div>
              ) : Object.keys(availableMuscleGroups).length === 0 ? (
                <div style={{ 
                  padding: '20px', 
                  textAlign: 'center', 
                  color: 'rgba(255, 255, 255, 0.5)' 
                }}>
                  Nessun gruppo muscolare disponibile
                </div>
              ) : (
                <div className="modal-muscle-groups-container">
                  <div className="muscle-groups-accordion">
                    {Object.entries(availableMuscleGroups).map(([macrocategoria, gruppi]) => {
                      const isExpanded = expandedCategories[macrocategoria] === true;
                      const currentGroups = Array.isArray(editData.gruppiMuscolari) ? editData.gruppiMuscolari : [];
                      const selectedCount = gruppi.filter(g => currentGroups.includes(g.id)).length;
                      
                      return (
                        <div key={macrocategoria} className="muscle-category-card">
                          <button
                            type="button"
                            className="muscle-category-header"
                            onClick={() => toggleCategory(macrocategoria)}
                          >
                            <div className="muscle-category-title-wrapper">
                              <span className="muscle-category-title">{macrocategoria}</span>
                              {selectedCount > 0 && (
                                <span className="muscle-category-badge">{selectedCount}</span>
                              )}
                            </div>
                            <span className={`muscle-category-arrow ${isExpanded ? 'expanded' : ''}`}>
                              ▼
                            </span>
                          </button>
                          {isExpanded && (
                            <div className="muscle-groups-grid">
                              {gruppi.map((gruppo) => {
                                const currentGroups = Array.isArray(editData.gruppiMuscolari) ? editData.gruppiMuscolari : [];
                                const isChecked = currentGroups.includes(gruppo.id);
                                return (
                                  <label
                                    key={gruppo.id}
                                    className={`muscle-group-checkbox-label ${isChecked ? 'checked' : ''}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => handleToggleMuscleGroup(gruppo.id)}
                                      className="muscle-group-checkbox"
                                    />
                                    <span className="muscle-group-name">{gruppo.nome}</span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {Array.isArray(editData.gruppiMuscolari) && editData.gruppiMuscolari.length > 0 && (
                    <div className="muscle-filters-summary">
                      <span className="muscle-filters-count">
                        {editData.gruppiMuscolari.length} {editData.gruppiMuscolari.length === 1 ? 'gruppo selezionato' : 'gruppi selezionati'}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </form>
      ) : (
        /* Modalità Visualizzazione */
        <>
        <div className={`two-column-layout ${isMobile ? 'mobile' : ''}`}>
            {/* Fotografia dello strumento */}
            <div className="info-box">
              <h3 className="info-box-title">
                Fotografia
              </h3>
              <div style={{
                width: '100%',
                aspectRatio: '4/3',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(235, 205, 97, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '10px',
                overflow: 'hidden'
              }}>
                {currentMachine.image_url ? (
                  <img 
                    key={currentMachine.image_url} 
                    src={`${API_URL}${currentMachine.image_url}`} 
                    alt={currentMachine.nome} 
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'contain',
                      borderRadius: '4px'
                    }} 
                    onError={(e) => { 
                      e.target.style.display = 'none'; 
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : (
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    gap: '16px',
                    color: 'rgba(255, 255, 255, 0.3)'
                  }}>
                    <MdImage size={64} style={{ color: 'rgba(235, 205, 97, 0.2)' }} />
                    <p style={{ margin: 0, fontSize: '14px' }}>Nessuna immagine</p>
                  </div>
                )}
                {/* Fallback nascosto */}
                <div style={{ 
                  display: 'none', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  gap: '16px',
                  color: 'rgba(255, 255, 255, 0.3)'
                }}>
                  <MdImage size={64} style={{ color: 'rgba(235, 205, 97, 0.2)' }} />
                  <p style={{ margin: 0, fontSize: '14px' }}>Immagine non disponibile</p>
                </div>
              </div>
            </div>

          {/* Informazioni generali */}
          <div className="info-box">
            <h3 className="info-box-title">
              Informazioni Generali
            </h3>
            <div className="info-list">
              <div className="info-item">
                <MdFitnessCenter className="info-icon" />
                <span className="info-text">
                  <strong>Nome:</strong> {currentMachine.nome}
                </span>
              </div>
              {currentMachine.descrizione && (
                <div className="info-item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <MdFitnessCenter className="info-icon" />
                    <strong style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Descrizione:</strong>
                  </div>
                  <p style={{ 
                    color: 'rgba(255, 255, 255, 0.7)', 
                    margin: 0, 
                    marginLeft: '32px',
                    lineHeight: '1.6'
                  }}>
                    {currentMachine.descrizione}
                  </p>
                </div>
              )}
              {currentMachine.created_at && (
                <div className="info-item">
                  <MdCalendarToday className="info-icon" />
                  <span className="info-text">
                    <strong>Creato il:</strong> {new Date(currentMachine.created_at).toLocaleDateString('it-IT', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              )}
              {currentMachine.updated_at && currentMachine.updated_at !== currentMachine.created_at && (
                <div className="info-item">
                  <MdUpdate className="info-icon" />
                  <span className="info-text">
                    <strong>Aggiornato il:</strong> {new Date(currentMachine.updated_at).toLocaleDateString('it-IT', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              )}
              </div>
            </div>
          </div>

          {/* Gruppi Muscolari - Card separata */}
          <div className="info-box" style={{ marginTop: '30px' }}>
            <h3 className="info-box-title">
              Gruppi Muscolari
            </h3>
            {currentMachine.gruppiMuscolari && currentMachine.gruppiMuscolari.length > 0 ? (
              <div className="machine-groups-list" style={{ flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                {currentMachine.gruppiMuscolari.map((gruppo, index) => {
                  const nome = typeof gruppo === 'object' ? gruppo.nome : gruppo;
                  const macrocategoria = typeof gruppo === 'object' ? gruppo.macrocategoria : null;
                  return (
                    <span
                      key={typeof gruppo === 'object' ? gruppo.id : index}
                      className="badge machines-badge-large"
                      title={macrocategoria ? `Macrocategoria: ${macrocategoria}` : ''}
                      style={{ fontSize: '13px', padding: '6px 12px' }}
                    >
                      {nome}
                    </span>
                  );
                })}
              </div>
            ) : (
              <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic', marginTop: '12px' }}>
                Nessun gruppo muscolare associato
              </p>
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false })}
        onConfirm={() => {
          setDeleteConfirm({ isOpen: false });
          onDelete(currentMachine);
        }}
        title="Conferma Eliminazione"
        message={`Sei sicuro di voler eliminare il macchinario "${currentMachine.nome}"? Questa azione non può essere annullata.`}
        confirmText="Elimina"
        cancelText="Annulla"
        type="danger"
      />
    </>
  );
}

// Sezione Esercizi
function EserciziSection({ exercises = [], loading = false, error = '', exerciseGroups = [], machines = [], onRefresh }) {
  const navigate = useNavigate();
  const videoInputRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGruppo, setFilterGruppo] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [formData, setFormData] = useState({ nome: '', gruppo_muscolare: '', machine_id: '' });
  const [videoFile, setVideoFile] = useState(null);
  const [removeVideo, setRemoveVideo] = useState(false);
  const [videoError, setVideoError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [message, setMessage] = useState(error);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, exercise: null });

  const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50 MB
  const MAX_VIDEO_DURATION = 15; // secondi

  useEffect(() => {
    if (error) setMessage(error);
  }, [error]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (editingExercise) {
      setFormData({
        nome: editingExercise.nome || '',
        gruppo_muscolare: editingExercise.gruppo_muscolare || '',
        machine_id: editingExercise.machine_id != null ? String(editingExercise.machine_id) : ''
      });
      setVideoFile(null);
      setRemoveVideo(false);
      setVideoError('');
      setShowModal(true);
    }
  }, [editingExercise]);

  const filtered = exercises.filter(ex => {
    const matchSearch = !searchTerm || ex.nome.toLowerCase().includes(searchTerm.toLowerCase());
    const matchGruppo = !filterGruppo || ex.gruppo_muscolare === filterGruppo;
    return matchSearch && matchGruppo;
  });

  const getExerciseVideoUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const normalizedPath = url.startsWith('/') ? url : `/${url}`;
    if (normalizedPath.startsWith('/uploads/')) return `${API_URL}/api${normalizedPath}`;
    return `${API_URL}${normalizedPath}`;
  };

  const openAddModal = () => {
    setEditingExercise(null);
    setFormData({
      nome: '',
      gruppo_muscolare: exerciseGroups[0] || '',
      machine_id: ''
    });
    setVideoFile(null);
    setRemoveVideo(false);
    setVideoError('');
    setShowModal(true);
  };

  const validateVideoFile = (file) => {
    setVideoError('');
    if (file.size > MAX_VIDEO_SIZE) {
      setVideoError(`Il video non deve superare i 50 MB (attuale: ${(file.size / (1024 * 1024)).toFixed(1)} MB).`);
      return false;
    }
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        if (video.duration > MAX_VIDEO_DURATION) {
          setVideoError(`Il video non deve superare i 15 secondi (attuale: ${Math.ceil(video.duration)} s).`);
          resolve(false);
        } else {
          resolve(true);
        }
      };
      video.onerror = () => {
        setVideoError('Impossibile leggere il file video.');
        resolve(false);
      };
      video.src = URL.createObjectURL(file);
    });
  };

  const onVideoFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setVideoFile(null);
      setVideoError('');
      return;
    }
    if (!file.type.startsWith('video/')) {
      setVideoError('Seleziona un file video (es. MP4, WebM).');
      setVideoFile(null);
      return;
    }
    const ok = await validateVideoFile(file);
    if (ok) {
      setVideoFile(file);
      setRemoveVideo(false);
    } else {
      setVideoFile(null);
    }
  };

  const clearFileSelection = () => {
    setVideoFile(null);
    setVideoError('');
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };

  const handleRemoveVideoClick = () => {
    clearFileSelection();
    if (editingExercise?.video_url) {
      setRemoveVideo(true);
    } else {
      setRemoveVideo(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (videoError) return;
    setFormLoading(true);
    setMessage('');
    const token = localStorage.getItem('token');
    const url = editingExercise
      ? `${API_URL}/api/training/exercises/${editingExercise.id}`
      : `${API_URL}/api/training/exercises`;

    const useFormData = videoFile || (editingExercise && removeVideo);

    let response;
    if (useFormData) {
      const fd = new FormData();
      fd.append('nome', formData.nome.trim());
      fd.append('gruppo_muscolare', formData.gruppo_muscolare);
      fd.append('machine_id', formData.machine_id === '' ? '' : formData.machine_id);
      if (videoFile) fd.append('video', videoFile);
      if (editingExercise && removeVideo) fd.append('removeVideo', 'true');
      response = await fetch(url, {
        method: editingExercise ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
    } else {
      response = await fetch(url, {
        method: editingExercise ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nome: formData.nome.trim(),
          gruppo_muscolare: formData.gruppo_muscolare,
          machine_id: formData.machine_id === '' ? null : parseInt(formData.machine_id, 10)
        })
      });
    }

    try {
      const data = await response.json();
      if (response.ok) {
        setMessage(editingExercise ? 'Esercizio aggiornato con successo!' : 'Esercizio creato con successo!');
        setShowModal(false);
        setEditingExercise(null);
        if (onRefresh) onRefresh();
        setTimeout(() => setMessage(''), 2000);
      } else {
        setMessage(data.error || 'Errore operazione');
      }
    } catch (err) {
      setMessage('Errore di connessione al server');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = (exercise) => setDeleteConfirm({ isOpen: true, exercise });

  const confirmDelete = async () => {
    const exercise = deleteConfirm.exercise;
    if (!exercise) return;
    setDeleteConfirm({ isOpen: false, exercise: null });
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/training/exercises/${exercise.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setMessage('Esercizio eliminato con successo!');
        if (onRefresh) onRefresh();
        setTimeout(() => setMessage(''), 2000);
      } else {
        setMessage(data.error || 'Errore eliminazione');
      }
    } catch (err) {
      setMessage('Errore di connessione al server');
    }
  };

  return (
    <>
      <div className={`machines-header ${isMobile ? 'mobile' : ''}`}>
        <h1 className={`section-title ${isMobile ? 'mobile' : ''}`}>Esercizi</h1>
        <div className="machines-actions">
          <button type="button" onClick={openAddModal} className="btn-primary button-with-icon">
            <MdAdd className="icon-large" />
            <span>Aggiungi</span>
          </button>
        </div>
      </div>

      <div className="machines-search-container">
        <div className="machines-search-wrapper">
          <MdSearch className="search-icon" />
          <input
            type="text"
            placeholder="Cerca esercizi"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field input-search"
          />
        </div>
        <div className="machines-filters">
          <select
            value={filterGruppo}
            onChange={(e) => setFilterGruppo(e.target.value)}
            className="input-field select-field select-dark"
            style={{ minWidth: '180px', marginLeft: '12px' }}
          >
            <option value="">Tutti i gruppi</option>
            {exerciseGroups.map(g => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
        </div>
      </div>

      {message && (
        <div className={message.includes('successo') ? 'message-success' : 'message-error'} style={{
          padding: '12px 20px',
          marginBottom: '20px',
          borderRadius: '8px',
          fontSize: '14px',
          backgroundColor: message.includes('successo') ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
          border: `1px solid ${message.includes('successo') ? 'rgba(76, 175, 80, 0.4)' : 'rgba(244, 67, 54, 0.4)'}`,
          color: message.includes('successo') ? '#4caf50' : '#f44336'
        }}>
          {message}
        </div>
      )}

      {loading ? (
        <div className="loading-placeholder">Caricamento esercizi...</div>
      ) : (
        <div className={`machines-table-wrapper ${isMobile ? 'mobile' : ''}`}>
          <table className="machines-table">
            <thead>
              <tr className="machines-table-header">
                <th className="machines-table-cell">Nome</th>
                <th className="machines-table-cell">Gruppo muscolare</th>
                <th className="machines-table-cell">Macchinario</th>
                <th className="machines-table-cell">Video</th>
                <th className="machines-table-cell" style={{ width: '170px' }}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="machines-table-cell" style={{ textAlign: 'center', padding: '32px' }}>
                    {exercises.length === 0 ? 'Nessun esercizio. Aggiungi il primo!' : 'Nessun esercizio corrisponde ai filtri.'}
                  </td>
                </tr>
              ) : (
                filtered.map((ex) => (
                  <tr key={ex.id} className="machines-table-row">
                    <td className="machines-table-cell machines-table-cell-name">{ex.nome}</td>
                    <td className="machines-table-cell">{ex.gruppo_muscolare}</td>
                    <td className="machines-table-cell">{ex.machine_name || 'Corpo libero'}</td>
                    <td className="machines-table-cell">
                      {ex.video_url ? (
                        <a href={getExerciseVideoUrl(ex.video_url)} target="_blank" rel="noopener noreferrer" style={{ color: '#ebcd61', textDecoration: 'underline' }}>Guarda</a>
                      ) : (
                        <span style={{ color: 'rgba(255,255,255,0.4)' }}>—</span>
                      )}
                    </td>
                    <td className="machines-table-cell">
                      <button
                        type="button"
                        className="icon-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/dashboard/exercises/${ex.id}`);
                        }}
                        title="Dettaglio"
                      >
                        <MdVisibility />
                      </button>
                      <button
                        type="button"
                        className="icon-button"
                        onClick={() => setEditingExercise(ex)}
                        title="Modifica"
                      >
                        <MdEdit />
                      </button>
                      <button
                        type="button"
                        className="icon-button danger"
                        onClick={() => handleDelete(ex)}
                        title="Elimina"
                      >
                        <MdDelete />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setEditingExercise(null); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2 className="modal-title">{editingExercise ? 'Modifica esercizio' : 'Nuovo esercizio'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nome *</label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Nome esercizio"
                  required
                />
              </div>
              <div className="form-group">
                <label>Gruppo muscolare *</label>
                <select
                  name="gruppo_muscolare"
                  value={formData.gruppo_muscolare}
                  onChange={handleChange}
                  className="input-field select-field select-dark"
                  required
                >
                  <option value="">Seleziona gruppo</option>
                  {exerciseGroups.map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Macchinario (opzionale)</label>
                <select
                  name="machine_id"
                  value={formData.machine_id}
                  onChange={handleChange}
                  className="input-field select-field select-dark"
                >
                  <option value="">Nessuno (corpo libero)</option>
                  {machines.map(m => (
                    <option key={m.id} value={m.id}>{m.nome}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Video esecuzione (opzionale, max 15 s, 50 MB)</label>
                <div className="exercise-video-panel">
                  {editingExercise?.video_url && !videoFile && !removeVideo && (
                    <div className="exercise-video-preview-wrapper">
                      <video
                        src={getExerciseVideoUrl(editingExercise.video_url)}
                        controls
                        className="exercise-video-preview"
                      />
                      <p className="exercise-video-caption">Video attuale</p>
                    </div>
                  )}

                  {videoFile && (
                    <div className="exercise-video-status">
                      <span className="exercise-video-status-badge success">Nuovo video selezionato</span>
                      <span>
                        {videoFile.name} ({(videoFile.size / (1024 * 1024)).toFixed(1)} MB)
                      </span>
                    </div>
                  )}

                  {removeVideo && editingExercise?.video_url && !videoFile && (
                    <div className="exercise-video-status">
                      <span className="exercise-video-status-badge warning">Rimozione attiva</span>
                      <span>Il video attuale verra rimosso al salvataggio.</span>
                    </div>
                  )}

                  <div className="exercise-video-actions">
                    <button
                      type="button"
                      className="exercise-video-btn"
                      onClick={() => videoInputRef.current?.click()}
                    >
                      Carica / sostituisci video
                    </button>
                    <button
                      type="button"
                      className="exercise-video-btn danger"
                      onClick={handleRemoveVideoClick}
                    >
                      Rimuovi video
                    </button>
                    {removeVideo && editingExercise?.video_url && (
                      <button
                        type="button"
                        className="exercise-video-btn ghost"
                        onClick={() => setRemoveVideo(false)}
                      >
                        Ripristina video attuale
                      </button>
                    )}
                  </div>

                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*"
                    onChange={onVideoFileChange}
                    style={{ display: 'none' }}
                  />

                  {!editingExercise?.video_url && !videoFile && (
                    <p className="exercise-video-caption">Nessun video selezionato.</p>
                  )}

                  {videoError && <div className="exercise-video-error">{videoError}</div>}
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowModal(false); setEditingExercise(null); }}>
                  Annulla
                </button>
                <button type="submit" className="btn-primary" disabled={formLoading}>
                  {formLoading ? 'Salvataggio...' : (editingExercise ? 'Salva' : 'Aggiungi')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, exercise: null })}
        onConfirm={confirmDelete}
        title="Elimina esercizio"
        message={`Sei sicuro di voler eliminare "${deleteConfirm.exercise?.nome}"?`}
        confirmText="Elimina"
        cancelText="Annulla"
        type="danger"
      />
    </>
  );
}

// Sezione Schede
function SchedeSection({ clients = [], machinesData = [], exercisesData = [], exerciseGroups = [] }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState('list');
  
  // Stati per generazione AI
  const [genFormData, setGenFormData] = useState({
    clientId: '',
    goal: '',
    duration: 4,
    frequency: 3,
    level: 'Intermedio',
    notes: ''
  });
  const [genLoading, setGenLoading] = useState(false);
  const [generatedPlan, setGeneratedPlan] = useState(null);
  const [message, setMessage] = useState('');

  // Stati per lista schede
  const [savedPlans, setSavedPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, planId: null, planName: null });
  const [shareConfirm, setShareConfirm] = useState({ isOpen: false, email: null, clientName: null });
  const [emailInputDialog, setEmailInputDialog] = useState({ isOpen: false });

  // Stati per template
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [applyTemplateDialog, setApplyTemplateDialog] = useState({ isOpen: false, template: null });
  const [applyClientId, setApplyClientId] = useState('');
  const [applyClientSearch, setApplyClientSearch] = useState('');

  // Dati client per WorkoutPlanViewer (plicometria/circonferenze)
  const [planClientData, setPlanClientData] = useState(null);

  // Stati per draft/revisione
  const [draftMode, setDraftMode] = useState('preview'); // 'preview' | 'edit'
  const [revisionFeedback, setRevisionFeedback] = useState('');
  const [revisionCount, setRevisionCount] = useState(0);
  const [revisionLoading, setRevisionLoading] = useState(false);
  const MAX_REVISIONS = 5;

  // Stati per creazione manuale
  const [manualForm, setManualForm] = useState({
    clientId: '',
    nome: '',
    durata_settimane: 4,
    frequenza_settimanale: 3,
    livello: 'Intermedio',
    obiettivi: '',
    note: ''
  });
  const [manualPlan, setManualPlan] = useState(null);

  

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Carica dati quando si cambia tab
  useEffect(() => {
    if (activeTab === 'list') loadPlans();
    if (activeTab === 'templates') loadTemplates();
  }, [activeTab]);

  // Carica dati plicometria del cliente quando si seleziona una scheda
  useEffect(() => {
    if (!selectedPlan?.client_id) {
      setPlanClientData(null);
      return;
    }
    const fetchClientData = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/client/${selectedPlan.client_id}/plicometria`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        const m = data.measurements?.[0];
        if (!m) { setPlanClientData(null); return; }

        const client = clients.find(c => c.id === selectedPlan.client_id);
        const age = client?.eta || null;
        const sesso = client?.sesso || null;
        const peso = client?.peso ? parseFloat(client.peso) : null;

        const result = { peso, measurementDate: m.measurement_date };

        if (age && sesso && peso) {
          const bc = calculateAllBodyComposition(m, age, sesso, peso);
          const best = bc?.jacksonPollock7 || bc?.jacksonPollock3 || bc?.durninWomersley;
          if (best) {
            result.bodyComposition = {
              percentuale_grasso: Math.round(best.bodyFatPercentage * 10) / 10,
              massa_grassa_kg: best.fatMass !== null ? Math.round(best.fatMass * 10) / 10 : null,
              massa_magra_kg: best.leanMass !== null ? Math.round(best.leanMass * 10) / 10 : null,
            };
          }
        }

        result.pliche = {
          pettorale: parseFloat(m.pettorale) || null,
          tricipite: parseFloat(m.tricipite) || null,
          ascellare: parseFloat(m.ascellare) || null,
          scapolare: parseFloat(m.scapolare) || null,
          soprailiaca: parseFloat(m.soprailiaca) || null,
          addominale: parseFloat(m.addominale) || null,
          coscia: parseFloat(m.coscia) || null,
          bicipite: parseFloat(m.bicipite) || null,
        };

        result.circonferenze = {
          spalle: parseFloat(m.circ_spalle) || null,
          petto: parseFloat(m.circ_petto) || null,
          braccio: parseFloat(m.circ_braccio) || null,
          vita: parseFloat(m.circ_vita) || null,
          fianchi: parseFloat(m.circ_fianchi) || null,
          coscia: parseFloat(m.circ_coscia) || null,
          polpaccio: parseFloat(m.circ_polpaccio) || null,
        };

        const hasData = result.bodyComposition || result.pliche || Object.values(result.circonferenze).some(v => v !== null);
        setPlanClientData(hasData ? result : null);
      } catch (err) {
        console.error('Error fetching plan client data:', err);
        setPlanClientData(null);
      }
    };
    fetchClientData();
  }, [selectedPlan?.client_id, clients]);

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/plans/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Errore caricamento template:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadPlans = async () => {
    setLoadingPlans(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/plans`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSavedPlans(data.plans || []);
      }
    } catch (error) {
      console.error('Errore caricamento schede:', error);
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleGenerate = async (formData) => {
    if (!formData.isTemplate && (!formData.clientId || !formData.goal)) {
      setMessage('Seleziona un cliente e definisci un obiettivo.');
      return;
    }
    if (!formData.goal) {
      setMessage('Definisci un obiettivo.');
      return;
    }

    setGenFormData(formData);
    setGenLoading(true);
    setMessage('');
    setGeneratedPlan(null);
    setRevisionCount(0);
    setRevisionFeedback('');
    setDraftMode('preview');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/plans/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setGeneratedPlan(data.plan);
        setMessage('Scheda generata! Rivedi il draft prima di salvare.');
        setActiveTab('draft');
      } else {
        setMessage(data.error || 'Errore durante la generazione.');
      }
    } catch (error) {
      setMessage('Errore di connessione al server.');
    } finally {
      setGenLoading(false);
    }
  };

  const handleRevise = async () => {
    if (!revisionFeedback.trim() || !generatedPlan) return;
    if (revisionCount >= MAX_REVISIONS) {
      setMessage(`Hai raggiunto il limite di ${MAX_REVISIONS} revisioni. Puoi continuare a modificare manualmente.`);
      return;
    }

    setRevisionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/plans/revise`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          clientId: genFormData.clientId,
          currentPlan: generatedPlan,
          feedback: revisionFeedback,
          isTemplate: !!genFormData.isTemplate,
        })
      });

      const data = await response.json();
      if (response.ok) {
        setGeneratedPlan(data.plan);
        setRevisionCount(prev => prev + 1);
        setRevisionFeedback('');
        setMessage(`Revisione ${revisionCount + 1}/${MAX_REVISIONS} applicata.`);
        setDraftMode('preview');
      } else {
        setMessage(data.error || 'Errore durante la revisione.');
      }
    } catch (error) {
      setMessage('Errore di connessione al server.');
    } finally {
      setRevisionLoading(false);
    }
  };

  const handleConfirmAndSave = async () => {
    if (!generatedPlan) return;

    if (genFormData.isTemplate) {
      await handleSaveAsTemplate(generatedPlan);
      return;
    }

    await handleSave();
  };

  const handleSave = async () => {
    if (!generatedPlan) return;
    
    try {
      const token = localStorage.getItem('token');
      const selectedClient = clients.find(c => c.id === parseInt(genFormData.clientId));
      
      const planToSave = {
        client_id: genFormData.clientId,
        nome: generatedPlan.nome_scheda || `Scheda per ${selectedClient ? selectedClient.nome : 'Cliente'}`,
        obiettivi: genFormData.goal,
        durata_settimane: generatedPlan.durata_settimane,
        frequenza_settimanale: generatedPlan.frequenza_settimanale,
        livello: genFormData.level,
        scheda_json: generatedPlan,
        note: genFormData.notes,
        created_at: genFormData.created_at || new Date().toISOString(),
        data_scadenza: genFormData.data_scadenza || null
      };

      const response = await fetch(`${API_URL}/api/client/plans`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(planToSave)
      });

      if (response.ok) {
        const data = await response.json(); // Leggi risposta una volta sola
        setMessage('Scheda salvata correttamente nel database!');
        
        // Aggiorna la lista immediatamente
        if (data.plan) {
            setSavedPlans(prev => [data.plan, ...prev]);
        }

        setGeneratedPlan(null);
        setGenFormData({ ...genFormData, goal: '', notes: '' });
        setActiveTab('list'); // Vai alla lista per vedere la nuova scheda
      } else {
        const data = await response.json();
        setMessage(data.error || 'Errore durante il salvataggio.');
      }
    } catch (error) {
      setMessage('Errore durante il salvataggio.');
    }
  };

  const handleUpdatePlan = async (updatedJson) => {
    if (!selectedPlan) return;
    
    // Mostra caricamento
    setMessage('Salvataggio in corso...');
    
    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        scheda_json: updatedJson,
        nome: updatedJson.nome_scheda || selectedPlan.nome,
        durata_settimane: updatedJson.durata_settimane,
        frequenza_settimanale: updatedJson.frequenza_settimanale,
        // Altri campi se necessario
      };

      const response = await fetch(`${API_URL}/api/client/plans/${selectedPlan.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setMessage('Scheda salvata con successo!');
        
        // Aggiorna lo stato locale
        const updatedFullPlan = data.plan;
        
        // Aggiorna la lista delle schede salvate
        setSavedPlans(prevPlans => 
          prevPlans.map(p => p.id === updatedFullPlan.id ? updatedFullPlan : p)
        );
        
        // Aggiorna la scheda selezionata
        setSelectedPlan(updatedFullPlan);
        
        setTimeout(() => setMessage(''), 2000);
      } else {
        const errorData = await response.json();
        setMessage(`Errore durante il salvataggio: ${errorData.error || 'Errore sconosciuto'}`);
      }
    } catch (error) {
      console.error('Errore salvataggio scheda:', error);
      setMessage('Errore di connessione al server');
    }
  };

  const handleDeletePlan = async (planId) => {
    const plan = savedPlans.find(p => p.id === planId) || selectedPlan;
    setDeleteConfirm({ 
      isOpen: true, 
      planId: planId, 
      planName: plan?.nome || 'questa scheda' 
    });
  };

  const confirmDeletePlan = async () => {
    const { planId } = deleteConfirm;
    if (!planId) return;

    setDeleteConfirm({ isOpen: false, planId: null, planName: null });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/plans/${planId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setMessage('Scheda eliminata con successo!');
        setSavedPlans(prev => prev.filter(p => p.id !== planId));
        if (selectedPlan && selectedPlan.id === planId) {
          setSelectedPlan(null);
        }
        setTimeout(() => setMessage(''), 3000);
      } else {
        const data = await response.json();
        setMessage(`Errore eliminazione: ${data.error}`);
      }
    } catch (error) {
      setMessage('Errore di connessione');
    }
  };

  const handleSharePlan = async () => {
    if (!selectedPlan) return;
    
    let emailToSend = selectedPlan.client_email;

    if (emailToSend) {
        const clientName = `${selectedPlan.client_nome || ''} ${selectedPlan.client_cognome || ''}`.trim();
        setShareConfirm({ 
            isOpen: true, 
            email: emailToSend, 
            clientName: clientName || 'il cliente'
        });
    } else {
        // Apri dialog per inserire email
        setEmailInputDialog({ isOpen: true });
    }
  };

  const confirmSharePlan = async () => {
    const { email } = shareConfirm;
    if (!email) return;

    setShareConfirm({ isOpen: false, email: null, clientName: null });
    await sendEmailToPlan(email);
  };

  const handleEmailInputConfirm = async (email) => {
    setEmailInputDialog({ isOpen: false });
    await sendEmailToPlan(email);
  };

  // --- Creazione manuale ---
  const handleStartManualCreation = () => {
    setManualPlan(null);
    setManualForm({ clientId: '', nome: '', durata_settimane: 4, frequenza_settimanale: 3, livello: 'Intermedio', obiettivi: '', note: '' });
    setActiveTab('manual');
  };

  const handleManualFormSubmit = () => {
    if (!manualForm.clientId || !manualForm.nome) {
      setMessage('Seleziona un cliente e inserisci un nome per la scheda.');
      return;
    }
    const blankPlan = {
      nome_scheda: manualForm.nome,
      descrizione: manualForm.obiettivi || '',
      durata_settimane: parseInt(manualForm.durata_settimane) || 4,
      frequenza_settimanale: parseInt(manualForm.frequenza_settimanale) || 3,
      split_routine: '',
      giorni: Array.from({ length: parseInt(manualForm.frequenza_settimanale) || 3 }, (_, i) => ({
        giorno_numero: i + 1,
        nome_giorno: `Giorno ${i + 1}`,
        esercizi: []
      })),
      consigli_trainer: []
    };
    setManualPlan(blankPlan);
    setActiveTab('manual_edit');
  };

  const handleSaveManualPlan = async (planJson) => {
    try {
      const token = localStorage.getItem('token');
      const planToSave = {
        client_id: manualForm.clientId,
        nome: planJson.nome_scheda || manualForm.nome,
        obiettivi: manualForm.obiettivi,
        durata_settimane: planJson.durata_settimane,
        frequenza_settimanale: planJson.frequenza_settimanale,
        livello: manualForm.livello,
        scheda_json: planJson,
        note: manualForm.note,
      };
      const response = await fetch(`${API_URL}/api/client/plans`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(planToSave)
      });
      if (response.ok) {
        const data = await response.json();
        setMessage('Scheda salvata con successo!');
        if (data.plan) setSavedPlans(prev => [data.plan, ...prev]);
        setManualPlan(null);
        setActiveTab('list');
        setTimeout(() => setMessage(''), 3000);
      } else {
        const data = await response.json();
        setMessage(data.error || 'Errore durante il salvataggio.');
      }
    } catch (error) {
      setMessage('Errore di connessione al server.');
    }
  };

  // --- Template da zero ---
  const [templateForm, setTemplateForm] = useState({
    nome: '', durata_settimane: 4, frequenza_settimanale: 3, obiettivi: ''
  });
  const [templatePlan, setTemplatePlan] = useState(null);

  const handleStartTemplateCreation = () => {
    setTemplatePlan(null);
    setTemplateForm({ nome: '', durata_settimane: 4, frequenza_settimanale: 3, obiettivi: '' });
    setActiveTab('create_template');
  };

  const handleTemplateFormSubmit = () => {
    if (!templateForm.nome) {
      setMessage('Inserisci un nome per il template.');
      return;
    }
    const blankPlan = {
      nome_scheda: templateForm.nome,
      descrizione: templateForm.obiettivi || '',
      durata_settimane: parseInt(templateForm.durata_settimane) || 4,
      frequenza_settimanale: parseInt(templateForm.frequenza_settimanale) || 3,
      split_routine: '',
      giorni: Array.from({ length: parseInt(templateForm.frequenza_settimanale) || 3 }, (_, i) => ({
        giorno_numero: i + 1,
        nome_giorno: `Giorno ${i + 1}`,
        esercizi: []
      })),
      consigli_trainer: []
    };
    setTemplatePlan(blankPlan);
    setActiveTab('template_edit');
  };

  const handleSaveNewTemplate = async (planJson) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/plans/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          nome: planJson.nome_scheda || templateForm.nome,
          obiettivi: planJson.descrizione || templateForm.obiettivi || null,
          durata_settimane: planJson.durata_settimane,
          frequenza_settimanale: planJson.frequenza_settimanale,
          scheda_json: planJson,
        })
      });
      if (response.ok) {
        const data = await response.json();
        setMessage('Template creato con successo!');
        setTemplates(prev => [data.template, ...prev]);
        setTemplatePlan(null);
        setActiveTab('templates');
        setTimeout(() => setMessage(''), 3000);
      } else {
        const data = await response.json();
        setMessage(data.error || 'Errore durante la creazione del template.');
      }
    } catch (error) {
      setMessage('Errore di connessione al server.');
    }
  };

  // --- Template ---
  const handleSaveAsTemplate = async (planJson) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/plans/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          nome: planJson.nome_scheda || 'Template',
          obiettivi: planJson.descrizione || null,
          durata_settimane: planJson.durata_settimane,
          frequenza_settimanale: planJson.frequenza_settimanale,
          livello: null,
          scheda_json: planJson,
          note: null
        })
      });
      if (response.ok) {
        setMessage('Template salvato con successo!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        const data = await response.json();
        setMessage(data.error || 'Errore durante il salvataggio del template.');
      }
    } catch (error) {
      setMessage('Errore di connessione al server.');
    }
  };

  const handleApplyTemplate = async () => {
    const { template } = applyTemplateDialog;
    if (!template || !applyClientId) {
      setMessage('Seleziona un cliente.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/plans/templates/${template.id}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ client_id: parseInt(applyClientId) })
      });
      if (response.ok) {
        const data = await response.json();
        setMessage('Scheda creata dal template con successo!');
        setApplyTemplateDialog({ isOpen: false, template: null });
        setApplyClientId('');
        setApplyClientSearch('');
        if (data.plan) setSavedPlans(prev => [data.plan, ...prev]);
        setActiveTab('list');
        setTimeout(() => setMessage(''), 3000);
      } else {
        const data = await response.json();
        setMessage(data.error || 'Errore applicazione template.');
      }
    } catch (error) {
      setMessage('Errore di connessione al server.');
    }
  };

  const filteredClientsForTemplate = clients.filter((client) => {
    const query = applyClientSearch.trim().toLowerCase();
    if (!query) return true;
    const fullName = `${client.nome || ''} ${client.cognome || ''}`.toLowerCase();
    return fullName.includes(query) || (client.email || '').toLowerCase().includes(query);
  });

  const selectedApplyClient = clients.find((c) => String(c.id) === String(applyClientId));

  const handleDeleteTemplate = async (templateId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/plans/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setTemplates(prev => prev.filter(t => t.id !== templateId));
        if (selectedTemplate && selectedTemplate.id === templateId) setSelectedTemplate(null);
        setMessage('Template eliminato con successo!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      setMessage('Errore di connessione al server.');
    }
  };

  const sendEmailToPlan = async (emailToSend) => {
    if (!selectedPlan) return;
    
    setMessage(`Invio email a ${emailToSend} in corso...`);
    try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/client/plans/${selectedPlan.id}/share`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ email: emailToSend })
        });
        
        if (response.ok) {
            const data = await response.json();
            setMessage(`Email inviata correttamente a ${emailToSend}!`);
            
            // Aggiorna lo stato locale se il backend restituisce il piano aggiornato
            if (data.plan) {
              setSelectedPlan(prev => ({
                ...prev,
                last_email_sent_at: data.plan.last_email_sent_at,
                last_email_status: data.plan.last_email_status
              }));
              
              // Aggiorna anche la lista
              setSavedPlans(prev => prev.map(p => 
                p.id === selectedPlan.id 
                  ? { ...p, last_email_sent_at: data.plan.last_email_sent_at, last_email_status: data.plan.last_email_status } 
                  : p
              ));
            }
            
            setTimeout(() => setMessage(''), 3000);
        } else {
            const data = await response.json();
            setMessage(`Errore invio: ${data.error}`);
        }
    } catch (error) {
        setMessage('Errore di connessione');
    }
  };

  return (
    <>
      <div className={`section-header ${isMobile ? 'mobile' : ''}`}>
        <h1 className={`section-title ${isMobile ? 'mobile' : ''}`}>
          Schede di Allenamento
        </h1>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button 
            className={`btn ${activeTab === 'list' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => { setActiveTab('list'); setSelectedPlan(null); }}
          >
            Le mie Schede
          </button>
          <button 
            className={`btn ${activeTab === 'templates' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => { setActiveTab('templates'); setSelectedTemplate(null); }}
          >
            Template
          </button>
          <button 
            className={`btn ${activeTab === 'generate' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('generate')}
          >
            <MdAdd style={{ marginRight: '6px' }} />
            Genera AI
          </button>
          <button 
            className={`btn ${activeTab === 'manual' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={handleStartManualCreation}
          >
            <MdEdit style={{ marginRight: '6px' }} />
            Crea Manuale
          </button>
        </div>
      </div>

      {message && (
        <div className="message-box" style={{ 
          backgroundColor: message.includes('successo') || message.includes('correttamente') ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
          border: `1px solid ${message.includes('successo') || message.includes('correttamente') ? 'rgba(76, 175, 80, 0.4)' : 'rgba(244, 67, 54, 0.4)'}`,
          color: message.includes('successo') || message.includes('correttamente') ? '#4caf50' : '#f44336',
          marginBottom: '20px',
          padding: '15px',
          borderRadius: '8px'
        }}>
          {message}
        </div>
      )}

      {activeTab === 'generate' && (
        <div style={{ 
          height: isMobile ? 'calc(100vh - 120px)' : 'calc(100vh - 200px)', 
          minHeight: isMobile ? '500px' : '600px', 
          maxHeight: isMobile ? 'none' : '900px',
          width: '100%',
          position: isMobile ? 'relative' : 'static'
        }}>
          <ConversationalWorkoutGenerator
            clients={clients}
            onGenerate={handleGenerate}
            onCancel={() => setActiveTab('list')}
            isGeneratingExternal={genLoading}
          />
        </div>
      )}

      {activeTab === 'generate_template' && (
        <div style={{ 
          height: isMobile ? 'calc(100vh - 120px)' : 'calc(100vh - 200px)', 
          minHeight: isMobile ? '500px' : '600px', 
          maxHeight: isMobile ? 'none' : '900px',
          width: '100%',
          position: isMobile ? 'relative' : 'static'
        }}>
          <ConversationalWorkoutGenerator
            clients={clients}
            onGenerate={handleGenerate}
            onCancel={() => setActiveTab('templates')}
            isGeneratingExternal={genLoading}
            isTemplateMode={true}
          />
        </div>
      )}

      {/* DRAFT TAB - Preview + Revisione */}
      {activeTab === 'draft' && generatedPlan && (
        <div style={{ width: '100%' }}>
          {/* Draft Header */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: '16px', padding: '16px',
            background: 'rgba(235, 205, 97, 0.06)', borderRadius: '12px',
            border: '1px solid rgba(235, 205, 97, 0.15)'
          }}>
            <div>
              <h3 style={{ margin: 0, color: '#ebcd61', fontSize: '16px' }}>
                Draft - Revisione Scheda
              </h3>
              <span style={{ fontSize: '12px', color: '#999' }}>
                Revisioni: {revisionCount}/{MAX_REVISIONS} | Modalità: {draftMode === 'preview' ? 'Anteprima' : 'Modifica Manuale'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                className={`btn ${draftMode === 'preview' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setDraftMode('preview')}
                style={{ padding: '6px 14px', fontSize: '13px' }}
              >
                Anteprima
              </button>
              <button
                className={`btn ${draftMode === 'edit' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setDraftMode('edit')}
                style={{ padding: '6px 14px', fontSize: '13px' }}
              >
                Modifica Manuale
              </button>
              <button
                onClick={() => handleSaveAsTemplate(generatedPlan)}
                className="btn btn-secondary"
                style={{ padding: '6px 14px', fontSize: '13px' }}
              >
                Salva come Template
              </button>
              <button
                onClick={handleConfirmAndSave}
                className="btn"
                style={{
                  backgroundColor: '#4caf50', border: 'none', color: 'white',
                  padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600'
                }}
              >
                Conferma e Salva
              </button>
            </div>
          </div>

          {/* AI Revision Chat */}
          {draftMode === 'preview' && (
            <div style={{
              marginBottom: '16px', padding: '16px',
              background: 'rgba(255,255,255,0.02)', borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.08)'
            }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#ccc', fontSize: '14px' }}>
                Richiedi modifiche all'AI
              </h4>
              <div style={{ display: 'flex', gap: '8px' }}>
                <textarea
                  value={revisionFeedback}
                  onChange={(e) => setRevisionFeedback(e.target.value)}
                  placeholder={revisionCount >= MAX_REVISIONS
                    ? 'Limite revisioni raggiunto. Usa la modifica manuale.'
                    : 'Es: "Aggiungi più esercizi per i dorsali", "Riduci il volume del giorno 3", "Sostituisci gli stacchi con un altro esercizio"...'}
                  disabled={revisionCount >= MAX_REVISIONS || revisionLoading}
                  rows="2"
                  style={{
                    flex: 1, background: '#1e1e1e', border: '1px solid #333', borderRadius: '8px',
                    padding: '10px', color: '#fff', fontSize: '13px', resize: 'vertical',
                    fontFamily: 'inherit',
                  }}
                />
                <button
                  onClick={handleRevise}
                  disabled={!revisionFeedback.trim() || revisionCount >= MAX_REVISIONS || revisionLoading}
                  style={{
                    padding: '10px 20px', background: revisionLoading ? '#555' : '#ebcd61',
                    color: '#1b1b1b', border: 'none', borderRadius: '8px',
                    cursor: revisionFeedback.trim() && revisionCount < MAX_REVISIONS && !revisionLoading ? 'pointer' : 'not-allowed',
                    fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap',
                    opacity: (!revisionFeedback.trim() || revisionCount >= MAX_REVISIONS) ? 0.5 : 1,
                  }}
                >
                  {revisionLoading ? 'Revisione...' : 'Applica Modifica AI'}
                </button>
              </div>
            </div>
          )}

          {/* Plan Viewer */}
          <WorkoutPlanViewer
            plan={generatedPlan}
            machines={machinesData}
            exercises={exercisesData}
            exerciseGroups={exerciseGroups}
            onPlanChange={(updatedPlan) => setGeneratedPlan(updatedPlan)}
            onSave={handleConfirmAndSave}
            readOnly={draftMode === 'preview'}
            startInEditMode={draftMode === 'edit'}
          />
        </div>
      )}

      {/* Tab Creazione Manuale */}
      {activeTab === 'manual' && !manualPlan && (
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(235, 205, 97, 0.2)',
            borderRadius: '12px',
            padding: '32px'
          }}>
            <h2 style={{ color: '#ebcd61', marginTop: 0, marginBottom: '24px', fontSize: '20px' }}>Nuova Scheda Manuale</h2>
            
            <div className="form-group">
              <label>Cliente *</label>
              <select
                name="clientId"
                value={manualForm.clientId}
                onChange={(e) => setManualForm(prev => ({ ...prev, clientId: e.target.value }))}
                className="input-field select-field select-dark"
                required
              >
                <option value="">Seleziona un cliente</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.nome} {c.cognome}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Nome scheda *</label>
              <input
                type="text"
                value={manualForm.nome}
                onChange={(e) => setManualForm(prev => ({ ...prev, nome: e.target.value }))}
                className="input-field"
                placeholder="Es. Scheda Ipertrofia - Mese 1"
                required
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label>Durata (settimane)</label>
                <input
                  type="number"
                  value={manualForm.durata_settimane}
                  onChange={(e) => setManualForm(prev => ({ ...prev, durata_settimane: e.target.value }))}
                  className="input-field"
                  min="1" max="52"
                />
              </div>
              <div className="form-group">
                <label>Giorni a settimana</label>
                <input
                  type="number"
                  value={manualForm.frequenza_settimanale}
                  onChange={(e) => setManualForm(prev => ({ ...prev, frequenza_settimanale: e.target.value }))}
                  className="input-field"
                  min="1" max="7"
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label>Livello</label>
                <select
                  value={manualForm.livello}
                  onChange={(e) => setManualForm(prev => ({ ...prev, livello: e.target.value }))}
                  className="input-field select-field select-dark"
                >
                  <option value="Principiante">Principiante</option>
                  <option value="Intermedio">Intermedio</option>
                  <option value="Avanzato">Avanzato</option>
                </select>
              </div>
              <div className="form-group">
                <label>Obiettivi</label>
                <input
                  type="text"
                  value={manualForm.obiettivi}
                  onChange={(e) => setManualForm(prev => ({ ...prev, obiettivi: e.target.value }))}
                  className="input-field"
                  placeholder="Es. Ipertrofia, Forza..."
                />
              </div>
            </div>

            <div className="form-group">
              <label>Note</label>
              <textarea
                value={manualForm.note}
                onChange={(e) => setManualForm(prev => ({ ...prev, note: e.target.value }))}
                className="textarea-field"
                rows={3}
                placeholder="Note aggiuntive..."
              />
            </div>

            <div className="modal-actions" style={{ borderTop: 'none', paddingTop: '8px' }}>
              <button className="btn btn-secondary" onClick={() => setActiveTab('list')}>Annulla</button>
              <button className="btn-primary" onClick={handleManualFormSubmit}>
                Crea Scheda Vuota
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab Manual Edit - WorkoutPlanViewer in edit mode */}
      {activeTab === 'manual_edit' && manualPlan && (
        <div style={{ width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px', paddingBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <button
              onClick={() => { setManualPlan(null); setActiveTab('manual'); }}
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', fontSize: '20px', cursor: 'pointer', width: '40px', height: '40px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              title="Torna indietro"
            >
              <MdArrowBack />
            </button>
            <h2 style={{ margin: 0, color: '#ebcd61', fontSize: '22px', fontWeight: '700' }}>
              Creazione Manuale: {manualForm.nome}
            </h2>
          </div>
          <WorkoutPlanViewer
            plan={manualPlan}
            machines={machinesData}
            exercises={exercisesData}
            exerciseGroups={exerciseGroups}
            startInEditMode={true}
            onPlanChange={(updatedPlan) => setManualPlan(updatedPlan)}
            onSave={(planJson) => handleSaveManualPlan(planJson)}
          />
        </div>
      )}

      {/* Tab Crea Template - Form */}
      {activeTab === 'create_template' && !templatePlan && (
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(33, 150, 243, 0.2)',
            borderRadius: '12px',
            padding: '32px'
          }}>
            <h2 style={{ color: '#2196f3', marginTop: 0, marginBottom: '24px', fontSize: '20px' }}>Nuovo Template</h2>

            <div className="form-group">
              <label>Nome template *</label>
              <input
                type="text"
                value={templateForm.nome}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, nome: e.target.value }))}
                className="input-field"
                placeholder="Es. Template Push/Pull/Legs"
                required
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label>Durata (settimane)</label>
                <input
                  type="number"
                  value={templateForm.durata_settimane}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, durata_settimane: e.target.value }))}
                  className="input-field"
                  min="1" max="52"
                />
              </div>
              <div className="form-group">
                <label>Giorni a settimana</label>
                <input
                  type="number"
                  value={templateForm.frequenza_settimanale}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, frequenza_settimanale: e.target.value }))}
                  className="input-field"
                  min="1" max="7"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Descrizione / Obiettivi</label>
              <textarea
                value={templateForm.obiettivi}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, obiettivi: e.target.value }))}
                className="textarea-field"
                rows={3}
                placeholder="Descrizione del template..."
              />
            </div>

            <div className="modal-actions" style={{ borderTop: 'none', paddingTop: '8px' }}>
              <button className="btn btn-secondary" onClick={() => setActiveTab('templates')}>Annulla</button>
              <button className="btn-primary" onClick={handleTemplateFormSubmit}>
                Crea Template Vuoto
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab Template Edit - WorkoutPlanViewer in edit mode */}
      {activeTab === 'template_edit' && templatePlan && (
        <div style={{ width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px', paddingBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <button
              onClick={() => { setTemplatePlan(null); setActiveTab('create_template'); }}
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', fontSize: '20px', cursor: 'pointer', width: '40px', height: '40px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              title="Torna indietro"
            >
              <MdArrowBack />
            </button>
            <h2 style={{ margin: 0, color: '#2196f3', fontSize: '22px', fontWeight: '700' }}>
              Creazione Template: {templateForm.nome}
            </h2>
            <span className="badge" style={{ fontSize: '11px', background: 'rgba(33, 150, 243, 0.2)', border: '1px solid rgba(33, 150, 243, 0.4)', color: '#2196f3', fontWeight: '600' }}>Template</span>
          </div>
          <WorkoutPlanViewer
            plan={templatePlan}
            machines={machinesData}
            exercises={exercisesData}
            exerciseGroups={exerciseGroups}
            startInEditMode={true}
            onPlanChange={(updatedPlan) => setTemplatePlan(updatedPlan)}
            onSave={(planJson) => handleSaveNewTemplate(planJson)}
          />
        </div>
      )}

      {/* Tab Template */}
      {activeTab === 'templates' && (
        <>
          {!selectedTemplate ? (
            <>
              <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button className="btn btn-secondary" onClick={() => setActiveTab('generate_template')}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px' }}>
                  <MdSmartToy style={{ fontSize: '18px' }} />
                  <span>Genera con AI</span>
                </button>
                <button className="btn-primary button-with-icon" onClick={handleStartTemplateCreation}>
                  <MdAdd className="icon-large" />
                  <span>Crea Template</span>
                </button>
              </div>
              <div className="card-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
                {loadingTemplates ? (
                  <div className="loading-placeholder">Caricamento template...</div>
                ) : templates.length === 0 ? (
                  <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.6)' }}>
                    Nessun template salvato. Puoi salvare una scheda come template dalla sezione di creazione o modifica.
                  </div>
                ) : (
                  templates.map(tmpl => (
                    <div key={tmpl.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <h3 style={{ margin: 0, color: '#ebcd61', fontSize: '18px', fontWeight: '700' }}>{tmpl.nome}</h3>
                        <span className="badge" style={{ 
                          fontSize: '11px', 
                          background: 'rgba(33, 150, 243, 0.2)',
                          border: '1px solid rgba(33, 150, 243, 0.4)',
                          color: '#2196f3',
                          fontWeight: '600'
                        }}>Template</span>
                      </div>
                      {tmpl.obiettivi && (
                        <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>{tmpl.obiettivi}</p>
                      )}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {tmpl.durata_settimane && (
                          <span style={{ background: 'rgba(156, 39, 176, 0.2)', border: '1px solid rgba(156, 39, 176, 0.4)', borderRadius: '16px', padding: '4px 10px', fontSize: '12px', fontWeight: '600', color: '#9c27b0' }}>
                            {tmpl.durata_settimane} sett.
                          </span>
                        )}
                        {tmpl.frequenza_settimanale && (
                          <span style={{ background: 'rgba(255, 152, 0, 0.2)', border: '1px solid rgba(255, 152, 0, 0.4)', borderRadius: '16px', padding: '4px 10px', fontSize: '12px', fontWeight: '600', color: '#ff9800' }}>
                            {tmpl.frequenza_settimanale}x/sett
                          </span>
                        )}
                      </div>
                      <div style={{ marginTop: 'auto', paddingTop: '12px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <button className="icon-button danger" onClick={() => handleDeleteTemplate(tmpl.id)} title="Elimina template">
                          <MdDelete />
                        </button>
                        <button className="icon-button" onClick={() => setSelectedTemplate(tmpl)} title="Vedi template">
                          <MdEdit />
                        </button>
                        <button 
                          className="btn-primary" 
                          style={{ fontSize: '13px', padding: '8px 16px', borderRadius: '6px' }}
                          onClick={() => {
                            setApplyTemplateDialog({ isOpen: true, template: tmpl });
                            setApplyClientId('');
                            setApplyClientSearch('');
                          }}
                        >
                          Usa Template
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Dialog per applicare template a un cliente */}
              {applyTemplateDialog.isOpen && (
                <div className="modal-overlay" onClick={() => { setApplyTemplateDialog({ isOpen: false, template: null }); setApplyClientSearch(''); }}>
                  <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                    <h2 className="modal-title">Applica Template: {applyTemplateDialog.template?.nome}</h2>
                    <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '20px' }}>
                      Seleziona il cliente a cui assegnare una nuova scheda basata su questo template.
                    </p>
                    <div className="form-group">
                      <label>Cliente *</label>
                      <div className="template-client-picker">
                        <div className="template-client-search-wrapper">
                          <MdSearch className="template-client-search-icon" />
                          <input
                            type="text"
                            className="input-field template-client-search-input"
                            placeholder="Cerca cliente per nome, cognome o email..."
                            value={applyClientSearch}
                            onChange={(e) => setApplyClientSearch(e.target.value)}
                          />
                          {applyClientSearch && (
                            <button
                              type="button"
                              className="template-client-clear-btn"
                              onClick={() => setApplyClientSearch('')}
                              title="Cancella ricerca"
                            >
                              <MdClear />
                            </button>
                          )}
                        </div>

                        <div className="template-client-list">
                          {filteredClientsForTemplate.length === 0 ? (
                            <div className="template-client-empty">Nessun cliente trovato.</div>
                          ) : (
                            filteredClientsForTemplate.map((client) => {
                              const isSelected = String(applyClientId) === String(client.id);
                              return (
                                <button
                                  key={client.id}
                                  type="button"
                                  className={`template-client-item ${isSelected ? 'selected' : ''}`}
                                  onClick={() => setApplyClientId(String(client.id))}
                                >
                                  <span className="template-client-name">{client.nome} {client.cognome}</span>
                                  {client.email && <span className="template-client-email">{client.email}</span>}
                                </button>
                              );
                            })
                          )}
                        </div>

                        {selectedApplyClient && (
                          <div className="template-client-selected">
                            Selezionato: <strong>{selectedApplyClient.nome} {selectedApplyClient.cognome}</strong>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="modal-actions">
                      <button className="btn-secondary" onClick={() => { setApplyTemplateDialog({ isOpen: false, template: null }); setApplyClientSearch(''); }}>Annulla</button>
                      <button className="btn-primary" onClick={handleApplyTemplate} disabled={!applyClientId}>Crea Scheda</button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px', paddingBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <button
                  onClick={() => setSelectedTemplate(null)}
                  style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', fontSize: '20px', cursor: 'pointer', width: '40px', height: '40px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  title="Torna ai template"
                >
                  <MdArrowBack />
                </button>
                <h2 style={{ margin: 0, color: '#ebcd61', fontSize: '24px', fontWeight: '700' }}>{selectedTemplate.nome}</h2>
                <span className="badge" style={{ fontSize: '11px', background: 'rgba(33, 150, 243, 0.2)', border: '1px solid rgba(33, 150, 243, 0.4)', color: '#2196f3', fontWeight: '600' }}>Template</span>
              </div>
              {selectedTemplate.scheda_json ? (
                <WorkoutPlanViewer 
                  plan={selectedTemplate.scheda_json}
                  machines={machinesData}
                  exercises={exercisesData}
                  exerciseGroups={exerciseGroups}
                  onPlanChange={() => {}}
                  onSave={async (updatedJson) => {
                    try {
                      const token = localStorage.getItem('token');
                      const response = await fetch(`${API_URL}/api/client/plans/${selectedTemplate.id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: JSON.stringify({ scheda_json: updatedJson, nome: updatedJson.nome_scheda || selectedTemplate.nome })
                      });
                      if (response.ok) {
                        const data = await response.json();
                        setSelectedTemplate(data.plan);
                        setTemplates(prev => prev.map(t => t.id === data.plan.id ? data.plan : t));
                        setMessage('Template aggiornato con successo!');
                        setTimeout(() => setMessage(''), 2000);
                      }
                    } catch (error) {
                      setMessage('Errore aggiornamento template.');
                    }
                  }}
                />
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
                  Nessun contenuto nel template.
                </div>
              )}
            </div>
          )}
        </>
      )}

      {activeTab === 'list' && (
        <>
          {!selectedPlan ? (
            <>
              <ClientFilter
                clients={clients}
                selectedClientIds={selectedClientIds}
                onSelectionChange={setSelectedClientIds}
              />
              
          <div className="card-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
            {loadingPlans ? (
              <div style={{ colSpan: '3', textAlign: 'center', padding: '20px', color: 'white' }}>Caricamento...</div>
            ) : (() => {
              // Filtra le schede in base ai clienti selezionati
              const filteredPlans = selectedClientIds.length === 0
                ? savedPlans
                : savedPlans.filter(plan => {
                    // Gestisce sia ID numerici che stringhe
                    const planClientId = typeof plan.client_id === 'number' ? plan.client_id : parseInt(plan.client_id);
                    return selectedClientIds.includes(planClientId) || selectedClientIds.includes(plan.client_id);
                  });
              
              return filteredPlans.length === 0 ? (
              <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.6)' }}>
                  {selectedClientIds.length === 0
                    ? 'Nessuna scheda salvata. Vai su "Crea nuova scheda" per crearne una.'
                    : 'Nessuna scheda trovata per i clienti selezionati.'}
              </div>
            ) : (
                filteredPlans.map(plan => (
                <div key={plan.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h3 style={{ margin: 0, color: '#ebcd61', fontSize: '18px', fontWeight: '700' }}>{plan.nome}</h3>
                    <span className="badge" style={{ 
                      fontSize: '12px', 
                      background: 'rgba(235, 205, 97, 0.2)', 
                      border: '1px solid rgba(235, 205, 97, 0.4)',
                      color: '#ebcd61',
                      fontWeight: '600'
                    }}>{plan.livello}</span>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                    <p style={{ margin: 0, color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: '500' }}>
                      <strong style={{ color: 'rgba(255,255,255,0.6)', marginRight: '6px' }}>Cliente:</strong>
                      {plan.client_nome} {plan.client_cognome}
                  </p>
                    <p style={{ margin: 0, color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: '500' }}>
                      <strong style={{ color: 'rgba(255,255,255,0.6)', marginRight: '6px' }}>Obiettivo:</strong>
                      {plan.obiettivi}
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
                      <span style={{
                        background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.2) 0%, rgba(156, 39, 176, 0.3) 100%)',
                        border: '1px solid rgba(156, 39, 176, 0.4)',
                        borderRadius: '16px',
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#9c27b0',
                        whiteSpace: 'nowrap'
                      }}>
                        {plan.durata_settimane} sett.
                      </span>
                      <span style={{
                        background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.2) 0%, rgba(255, 152, 0, 0.3) 100%)',
                        border: '1px solid rgba(255, 152, 0, 0.4)',
                        borderRadius: '16px',
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#ff9800',
                        whiteSpace: 'nowrap'
                      }}>
                        {plan.frequenza_settimanale}x/sett
                      </span>
                    </div>
                    {plan.data_scadenza && (
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>
                          <strong style={{ color: 'rgba(255,255,255,0.5)' }}>Scadenza:</strong>{' '}
                          {new Date(plan.data_scadenza).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </p>
                        {plan.status && (
                          <span style={{
                            background: plan.status === 'Scaduta' 
                              ? 'rgba(244, 67, 54, 0.2)' 
                              : plan.status === 'In scadenza'
                              ? 'rgba(255, 152, 0, 0.2)'
                              : 'rgba(76, 175, 80, 0.2)',
                            border: `1px solid ${plan.status === 'Scaduta' 
                              ? 'rgba(244, 67, 54, 0.4)' 
                              : plan.status === 'In scadenza'
                              ? 'rgba(255, 152, 0, 0.4)'
                              : 'rgba(76, 175, 80, 0.4)'}`,
                            borderRadius: '16px',
                            padding: '4px 10px',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: plan.status === 'Scaduta' 
                              ? '#f44336' 
                              : plan.status === 'In scadenza'
                              ? '#ff9800'
                              : '#4caf50',
                            alignSelf: 'flex-start',
                            display: 'inline-block'
                          }}>
                            {plan.status}
                            {plan.giorni_rimanenti !== undefined && plan.giorni_rimanenti >= 0 && (
                              <span style={{ marginLeft: '6px', opacity: 0.8 }}>
                                ({plan.giorni_rimanenti} {plan.giorni_rimanenti === 1 ? 'giorno' : 'giorni'})
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div style={{ marginTop: 'auto', paddingTop: '12px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <button
                      className="btn-danger"
                      style={{
                        fontSize: '13px',
                        padding: '8px 12px',
                        background: 'rgba(244, 67, 54, 0.1)',
                        border: '1px solid rgba(244, 67, 54, 0.3)',
                        color: '#f44336',
                        fontWeight: '600',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePlan(plan.id);
                      }}
                      title="Elimina"
                    >
                      <MdDelete size={18} />
                    </button>
                    <button 
                      className="btn-secondary" 
                      style={{ 
                        fontSize: '13px', 
                        padding: '8px 16px',
                        background: 'rgba(235, 205, 97, 0.1)',
                        border: '1px solid rgba(235, 205, 97, 0.3)',
                        color: '#ebcd61',
                        fontWeight: '600',
                        borderRadius: '6px',
                        transition: 'all 0.3s ease'
                      }} 
                      onClick={() => setSelectedPlan(plan)}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(235, 205, 97, 0.2)';
                        e.target.style.borderColor = 'rgba(235, 205, 97, 0.5)';
                        e.target.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(235, 205, 97, 0.1)';
                        e.target.style.borderColor = 'rgba(235, 205, 97, 0.3)';
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      Dettagli
                    </button>
                  </div>
                </div>
              ))
              );
            })()}
          </div>
            </>
          ) : (
            <div style={{ width: '100%' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '24px',
                paddingBottom: '20px',
                borderBottom: '1px solid rgba(255,255,255,0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <button
                    onClick={() => setSelectedPlan(null)}
                    style={{
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: 'white',
                      fontSize: '20px',
                      cursor: 'pointer',
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(255,255,255,0.2)';
                      e.target.style.transform = 'translateX(-4px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'rgba(255,255,255,0.1)';
                      e.target.style.transform = 'translateX(0)';
                    }}
                    title="Torna alla lista"
                  >
                    <MdArrowBack />
                  </button>
                  <h2 style={{ margin: 0, color: '#ebcd61', fontSize: '24px', fontWeight: '700' }}>
                    {selectedPlan.nome}
                  </h2>
                    </div>
                  </div>

                  {/* Informazioni scheda - solo visualizzazione */}
                  <div style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(235, 205, 97, 0.2)',
                    borderRadius: '12px',
                    padding: '24px',
                    marginBottom: '24px'
                  }}>
                    <h3 style={{ 
                      margin: '0 0 20px 0', 
                      color: '#ebcd61', 
                      fontSize: '18px', 
                      fontWeight: '600',
                      borderBottom: '1px solid rgba(235, 205, 97, 0.2)',
                      paddingBottom: '12px'
                    }}>
                      Informazioni scheda
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px' }}>
                      {/* Proprietario */}
                      <div>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '8px', 
                          color: 'rgba(255,255,255,0.6)', 
                          fontSize: '12px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: '500'
                        }}>
                          Proprietario
                        </label>
                        <div style={{
                          width: '100%',
                          padding: '12px',
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '15px',
                          fontWeight: '500'
                        }}>
                          {selectedPlan.client_nome && selectedPlan.client_cognome 
                            ? `${selectedPlan.client_nome} ${selectedPlan.client_cognome}`
                            : selectedPlan.client_nome || selectedPlan.client_cognome || 'N/D'}
                        </div>
                      </div>
                      
                      {/* Data di Creazione */}
                      <div>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '8px', 
                          color: 'rgba(255,255,255,0.6)', 
                          fontSize: '12px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: '500'
                        }}>
                          Data di Creazione
                        </label>
                        <div style={{
                          width: '100%',
                          padding: '12px',
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '15px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <MdCalendarToday size={18} style={{ color: 'rgba(255,255,255,0.5)' }} />
                          {selectedPlan.created_at 
                            ? new Date(selectedPlan.created_at).toLocaleDateString('it-IT', { 
                                day: '2-digit', 
                                month: 'short', 
                                year: 'numeric' 
                              })
                            : 'N/D'}
                        </div>
                      </div>
                      
                      {/* Durata */}
                      <div>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '8px', 
                          color: 'rgba(255,255,255,0.6)', 
                          fontSize: '12px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: '500'
                        }}>
                          Durata (settimane)
                        </label>
                        <div style={{
                          width: '100%',
                          padding: '12px',
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '15px',
                          fontWeight: '500'
                        }}>
                          {selectedPlan.durata_settimane || 'N/D'} {selectedPlan.durata_settimane ? 'settimane' : ''}
                        </div>
                      </div>
                      
                      {/* Scadenza calcolata */}
                      {selectedPlan.data_scadenza && (
                        <div>
                          <label style={{ 
                            display: 'block', 
                            marginBottom: '8px', 
                            color: 'rgba(255,255,255,0.6)', 
                            fontSize: '12px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontWeight: '500'
                          }}>
                            Scadenza calcolata
                          </label>
                          <div style={{
                            width: '100%',
                            padding: '12px',
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '8px',
                            color: '#fff',
                            fontSize: '15px',
                            fontWeight: '500'
                          }}>
                            {new Date(selectedPlan.data_scadenza).toLocaleDateString('it-IT', { 
                              day: '2-digit', 
                              month: 'long', 
                              year: 'numeric' 
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Status */}
                    <div style={{ 
                      marginTop: '20px', 
                      paddingTop: '20px',
                      borderTop: '1px solid rgba(235, 205, 97, 0.2)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px'
                    }}>
                      {selectedPlan.status && (
                        <div>
                          <label style={{ 
                            display: 'block', 
                            marginBottom: '10px', 
                            color: 'rgba(255,255,255,0.6)', 
                            fontSize: '12px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontWeight: '500'
                          }}>
                            Status
                          </label>
                          <span style={{
                            background: selectedPlan.status === 'Scaduta' 
                              ? 'rgba(244, 67, 54, 0.15)' 
                              : selectedPlan.status === 'In scadenza'
                              ? 'rgba(255, 152, 0, 0.15)'
                              : 'rgba(76, 175, 80, 0.15)',
                            border: `1px solid ${selectedPlan.status === 'Scaduta' 
                              ? 'rgba(244, 67, 54, 0.4)' 
                              : selectedPlan.status === 'In scadenza'
                              ? 'rgba(255, 152, 0, 0.4)'
                              : 'rgba(76, 175, 80, 0.4)'}`,
                            borderRadius: '8px',
                            padding: '10px 16px',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: selectedPlan.status === 'Scaduta' 
                              ? '#f44336' 
                              : selectedPlan.status === 'In scadenza'
                              ? '#ff9800'
                              : '#4caf50',
                            display: 'inline-block'
                          }}>
                            {selectedPlan.status}
                            {selectedPlan.giorni_rimanenti !== undefined && selectedPlan.giorni_rimanenti >= 0 && (
                              <span style={{ marginLeft: '8px', opacity: 0.9, fontWeight: '500' }}>
                                ({selectedPlan.giorni_rimanenti} {selectedPlan.giorni_rimanenti === 1 ? 'giorno' : 'giorni'})
                              </span>
                            )}
                          </span>
                        </div>
                      )}
                      
                      {/* Status Email */}
                      <div>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '10px', 
                          color: 'rgba(255,255,255,0.6)', 
                          fontSize: '12px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: '500'
                        }}>
                          Invio Email
                        </label>
                        {selectedPlan.last_email_sent_at ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            <span style={{
                              background: selectedPlan.last_email_status === 'Inviato'
                                ? 'rgba(76, 175, 80, 0.15)'
                                : 'rgba(244, 67, 54, 0.15)',
                              border: `1px solid ${selectedPlan.last_email_status === 'Inviato'
                                ? 'rgba(76, 175, 80, 0.4)'
                                : 'rgba(244, 67, 54, 0.4)'}`,
                              borderRadius: '8px',
                              padding: '10px 16px',
                              fontSize: '14px',
                              fontWeight: '600',
                              color: selectedPlan.last_email_status === 'Inviato'
                                ? '#4caf50'
                                : '#f44336',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              <MdEmail size={18} />
                              {selectedPlan.last_email_status === 'Inviato' ? 'Inviata' : selectedPlan.last_email_status || 'Inviata'}
                            </span>
                            <span style={{
                              color: 'rgba(255,255,255,0.7)',
                              fontSize: '13px',
                              fontStyle: 'italic'
                            }}>
                              il {new Date(selectedPlan.last_email_sent_at).toLocaleString('it-IT', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        ) : (
                          <span style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '8px',
                            padding: '10px 16px',
                            fontSize: '14px',
                            fontWeight: '500',
                            color: 'rgba(255,255,255,0.5)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <MdEmail size={18} />
                            Non inviata
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

              <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-secondary"
                  style={{ fontSize: '13px', padding: '8px 16px' }}
                  onClick={() => handleSaveAsTemplate(selectedPlan.scheda_json)}
                >
                  Salva come Template
                </button>
              </div>
              {selectedPlan.scheda_json ? (
                <WorkoutPlanViewer 
                  plan={selectedPlan.scheda_json}
                  machines={machinesData}
                  exercises={exercisesData}
                  exerciseGroups={exerciseGroups}
                  onPlanChange={(updatedPlan) => {}}
                  onSave={handleUpdatePlan}
                  onShare={handleSharePlan}
                  onDelete={() => handleDeletePlan(selectedPlan.id)}
                  lastEmailSent={selectedPlan.last_email_sent_at}
                  lastEmailStatus={selectedPlan.last_email_status}
                  clientData={planClientData}
                />
              ) : (
                  <div style={{ 
                  padding: '40px', 
                  textAlign: 'center', 
                  color: 'rgba(255,255,255,0.6)',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '12px'
                  }}>
                  <p>Nessun contenuto della scheda disponibile.</p>
                  </div>
              )}
            </div>
          )}

          <ConfirmDialog
            isOpen={deleteConfirm.isOpen}
            onClose={() => setDeleteConfirm({ isOpen: false, planId: null, planName: null })}
            onConfirm={confirmDeletePlan}
            title="Conferma Eliminazione"
            message={`Sei sicuro di voler eliminare la scheda "${deleteConfirm.planName}"? Questa azione non può essere annullata.`}
            confirmText="Elimina"
            cancelText="Annulla"
            type="danger"
          />

          <ConfirmDialog
            isOpen={shareConfirm.isOpen}
            onClose={() => setShareConfirm({ isOpen: false, email: null, clientName: null })}
            onConfirm={confirmSharePlan}
            title="Conferma Invio Email"
            message={`Inviare la scheda "${selectedPlan?.nome || 'di allenamento'}" a ${shareConfirm.clientName} (${shareConfirm.email})?`}
            confirmText="Invia"
            cancelText="Annulla"
            type="info"
          />

          <EmailInputDialog
            isOpen={emailInputDialog.isOpen}
            onClose={() => setEmailInputDialog({ isOpen: false })}
            onConfirm={handleEmailInputConfirm}
            title="Inserisci Email"
            message="Il cliente non ha un'email associata. Inserisci l'indirizzo email a cui inviare la scheda."
            confirmText="Invia"
            cancelText="Annulla"
          />
        </>
      )}
    </>
  );
}

export default Dashboard;

